/* eslint-disable */
import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { X, Send, CheckCircle, Eye, EyeOff, Mail, Coffee, ChefHat, UtensilsCrossed, User, Calculator, ChevronDown, ChevronUp, AlertTriangle, RefreshCw, Check } from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer, ReferenceLine,
} from 'recharts';

const C = {
  yellow:'#F7EC71', yellowLight:'#FEFCE8', orange:'#E4AC49', orangeLight:'#FDF3DC',
  green:'#7EA594', greenLight:'#EAF2EF', cream:'#F7F6EF', charcoal:'#2F3A42',
  navy:'#445366', mid:'#7A8A96', border:'#D8DEE3', skyBlue:'#BCD9E5',
  skyBlueLight:'#EAF4F8', skyText:'#3A8FA3', white:'#FFFFFF',
  red:'#DC3545', redLight:'#FFF0F0', amber:'#E07B00', amberLight:'#FFF3E0',
  purple:'#7C3AED', purpleLight:'#F3EEFF',
};

const VENUES = ['Box Jordaan', 'Box Plantage', 'Sassys', 'Prep Kitchen'];
const DEPTS  = ['FOH', 'KIT', 'Management'];
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const VENUE_COLORS = {
  'Box Jordaan':  '#445366',
  'Box Plantage': '#3A8FA3',
  'Sassys':       '#E07B00',
  'Prep Kitchen': '#7EA594',
};

const r1 = v => Math.round(parseFloat(v) * 10) / 10;

const pill = (active, sm = false) => ({
  padding: sm ? '4px 10px' : '6px 14px', borderRadius: '20px',
  border: `1px solid ${active ? C.navy : C.border}`, cursor: 'pointer',
  fontSize: sm ? '12px' : '13px', fontWeight: active ? 600 : 400,
  background: active ? C.navy : C.white, color: active ? C.white : C.mid,
});

function RoleIcon({ dept, size = 32 }) {
  const map = {
    FOH:        { bg: C.skyBlueLight, c: C.skyText },
    KIT:        { bg: C.orangeLight,  c: C.amber   },
    Management: { bg: C.purpleLight,  c: C.purple  },
  };
  const s = map[dept] || { bg: C.cream, c: C.mid };
  const sz = size * 0.44;
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: s.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: `1px solid ${s.c}22` }}>
      {dept === 'FOH'        && <Coffee size={sz}          strokeWidth={1.8} color={s.c} />}
      {dept === 'KIT'        && <ChefHat size={sz}         strokeWidth={1.8} color={s.c} />}
      {dept === 'Management' && <UtensilsCrossed size={sz} strokeWidth={1.8} color={s.c} />}
      {!['FOH','KIT','Management'].includes(dept) && <User size={sz} strokeWidth={1.8} color={s.c} />}
    </div>
  );
}

function BalancePill({ value, type }) {
  if (value === null || value === undefined || value === '') return <span style={{ color: C.border }}>—</span>;
  const n = r1(value);
  const bg = n > 0 ? C.skyBlueLight : n < 0 ? C.redLight : C.cream;
  return (
    <span style={{ background: bg, color: C.charcoal, padding: '3px 10px', borderRadius: '8px', fontSize: '14px', fontWeight: 700, border: `1px solid ${C.border}` }}>
      {n}h
    </span>
  );
}

function genToken() {
  return Math.random().toString(36).substring(2, 10) + Math.random().toString(36).substring(2, 10);
}

function fmtDT(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: C.charcoal, borderRadius: '8px', padding: '10px 14px', boxShadow: '0 4px 16px rgba(0,0,0,0.2)', fontSize: '12px' }}>
      <div style={{ color: C.skyBlue, fontWeight: 700, marginBottom: '6px' }}>{label}</div>
      {payload.map(p => (
        <div key={p.name} style={{ color: C.white, display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '2px' }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.color, display: 'inline-block' }}/>
          <span style={{ color: '#aaa' }}>{p.name}:</span>
          <span style={{ fontWeight: 700 }}>{typeof p.value === 'number' && p.value > 0 ? `+${r1(p.value)}` : r1(p.value)}h</span>
        </div>
      ))}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// VENUE TREND CHART
// ══════════════════════════════════════════════════════════════════════
function VenueTrendChart({ members, entries, snaps }) {
  const ft = members.filter(m => m.employment_type === 'Full Time');

  const sorted = [...snaps]
    .sort((a, b) => a.year !== b.year ? a.year - b.year : a.month - b.month)
    .slice(-8);

  const chartData = sorted.map(snap => {
    const snapEntries = entries.filter(e => e.snapshot_id === snap.id);
    const point = { label: `${MONTHS[snap.month - 1]} ${String(snap.year).slice(2)}` };
    VENUES.forEach(venue => {
      const vMembers = ft.filter(m => m.venue === venue);
      const vEntries = vMembers.map(m => snapEntries.find(e => e.team_member_id === m.id)).filter(Boolean);
      if (vEntries.length > 0) {
        const deltas = vEntries.map(e => {
          if (!e.notes) return null;
          const match = e.notes.match(/ot_delta=([^;]+)/);
          return match ? parseFloat(match[1]) : null;
        }).filter(v => v !== null);
        if (deltas.length > 0) {
          const avg = deltas.reduce((s, v) => s + v, 0) / deltas.length;
          point[venue.replace('Box ', '')] = r1(avg);
        }
      }
    });
    return point;
  });

  const activeVenues = VENUES.filter(v => chartData.some(d => d[v.replace('Box ', '')] !== undefined));
  if (activeVenues.length === 0) return null;

  return (
    <div style={{ background: C.white, borderRadius: '12px', border: `1px solid ${C.border}`, padding: '18px 20px', marginBottom: '20px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '4px' }}>
        <div style={{ fontSize: '13px', fontWeight: 700, color: C.charcoal }}>Average Monthly OT Movement by Venue</div>
        <div style={{ fontSize: '11px', color: C.mid }}>last 8 months</div>
      </div>
      <div style={{ fontSize: '11px', color: C.mid, marginBottom: '16px' }}>
        Average hours over/under contract per person per venue each month — positive means working above contract
      </div>
      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={chartData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
          <XAxis dataKey="label" tick={{ fontSize: 11, fill: C.mid }} />
          <YAxis tick={{ fontSize: 11, fill: C.mid }} tickFormatter={v => `${v}h`} />
          <Tooltip content={<ChartTooltip />} />
          <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '8px' }} />
          <ReferenceLine y={0} stroke={C.red} strokeDasharray="4 4" strokeOpacity={0.4} />
          {activeVenues.map(venue => (
            <Line
              key={venue}
              type="monotone"
              dataKey={venue.replace('Box ', '')}
              stroke={VENUE_COLORS[venue]}
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
              connectNulls
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Bar chart tooltip ─────────────────────────────────────────────────
function BarTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((s, p) => s + (p.value || 0), 0);
  // Get contract from first payload entry's extra data
  const contract = payload[0]?.payload?.contract ?? null;
  return (
    <div style={{ background: C.charcoal, borderRadius: '8px', padding: '10px 14px', boxShadow: '0 4px 16px rgba(0,0,0,0.2)', fontSize: '12px', minWidth: '160px' }}>
      <div style={{ color: C.skyBlue, fontWeight: 700, marginBottom: '8px' }}>{label}</div>
      {payload.map(p => p.value > 0 && (
        <div key={p.name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', marginBottom: '3px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ width: 8, height: 8, borderRadius: '2px', background: p.fill, display: 'inline-block', flexShrink: 0 }}/>
            <span style={{ color: '#bbb' }}>{p.name}</span>
          </div>
          <span style={{ color: C.white, fontWeight: 600 }}>{r1(p.value)}h</span>
        </div>
      ))}
      <div style={{ borderTop: '1px solid rgba(255,255,255,0.12)', marginTop: '6px', paddingTop: '6px', display: 'flex', justifyContent: 'space-between' }}>
        <span style={{ color: '#bbb' }}>Total</span>
        <span style={{ color: C.white, fontWeight: 700 }}>{r1(total)}h</span>
      </div>
      {contract && (
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '3px' }}>
          <span style={{ color: '#bbb' }}>Contract</span>
          <span style={{ color: C.amber, fontWeight: 600 }}>{r1(contract)}h</span>
        </div>
      )}
      {contract && (
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '3px' }}>
          <span style={{ color: '#bbb' }}>OT this month</span>
          <span style={{ color: r1(total - contract) >= 0 ? C.amber : C.green, fontWeight: 700 }}>
            {r1(total - contract) >= 0 ? '+' : ''}{r1(total - contract)}h
          </span>
        </div>
      )}
    </div>
  );
}

// ── Hours breakdown stacked bar chart ─────────────────────────────────
function HoursBreakdownChart({ memberEntries }) {
  const BAR_COLORS = {
    worked:  '#4A7B8C',
    leave:   '#A8D5B5',
    sick:    '#E07B00',
    special: '#F4B8C1',
    unpaid:  '#8C8C8C',
  };

  const chartData = memberEntries.map(e => {
    const notes = e.notes || '';
    const get = key => {
      const m = notes.match(new RegExp(`${key}=([^;]+)`));
      return m ? parseFloat(m[1]) || 0 : 0;
    };
    return {
      label:    `${MONTHS[e.month - 1]} ${String(e.year).slice(2)}`,
      worked:   r1(get('worked')),
      leave:    r1(get('leave')),
      sick:     r1(get('sick')),
      special:  r1(get('special')),
      unpaid:   r1(get('unpaid')),
      contract: r1(get('contract')),
    };
  });

  // Use the contract hours from data (consistent across months)
  const contractHrs = chartData.length ? (Math.max(...chartData.map(d => d.contract)) || null) : null;

  if (chartData.length === 0) return null;

  return (
    <div>
      <div style={{ fontSize: '11px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>
        Hours Breakdown
      </div>
      <div style={{ fontSize: '11px', color: C.mid, marginBottom: '12px' }}>
        Stacked bars show makeup of total hours each month — dotted line is contracted hours
      </div>
      <div style={{ background: C.cream, borderRadius: '12px', padding: '16px', border: `1px solid ${C.border}` }}>
        {/* Legend */}
        <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', marginBottom: '12px' }}>
          {[
            { key: 'worked',  label: 'Worked'       },
            { key: 'leave',   label: 'Leave'         },
            { key: 'sick',    label: 'Sick'          },
            { key: 'special', label: 'Special Leave' },
            { key: 'unpaid',  label: 'No-pay Leave'  },
          ].map(({ key, label }) => (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: C.mid }}>
              <span style={{ width: 10, height: 10, borderRadius: '2px', background: BAR_COLORS[key], display: 'inline-block' }}/>
              {label}
            </div>
          ))}
          {contractHrs && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: C.mid }}>
              <span style={{ width: 14, height: 2, background: C.amber, display: 'inline-block', borderTop: `2px dashed ${C.amber}` }}/>
              Contract ({contractHrs}h)
            </div>
          )}
        </div>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={chartData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: C.mid }} />
            <YAxis tick={{ fontSize: 11, fill: C.mid }} tickFormatter={v => `${v}h`} />
            <Tooltip content={<BarTooltip />} />
            {contractHrs && (
              <ReferenceLine y={contractHrs} stroke={C.amber} strokeDasharray="5 3" strokeWidth={1.5} />
            )}
            <Bar dataKey="worked"  name="Worked"        stackId="a" fill={BAR_COLORS.worked}  radius={[0,0,0,0]} />
            <Bar dataKey="leave"   name="Leave"         stackId="a" fill={BAR_COLORS.leave}   radius={[0,0,0,0]} />
            <Bar dataKey="sick"    name="Sick"          stackId="a" fill={BAR_COLORS.sick}    radius={[0,0,0,0]} />
            <Bar dataKey="special" name="Special Leave" stackId="a" fill={BAR_COLORS.special} radius={[0,0,0,0]} />
            <Bar dataKey="unpaid"  name="No-pay Leave"  stackId="a" fill={BAR_COLORS.unpaid}  radius={[4,4,0,0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// EITJE & BALANCE SETTINGS — admin only, lives in the member modal
// ══════════════════════════════════════════════════════════════════════
function EitjeBalanceSettings({ member, currentUser, onSaved }) {
  const isAdmin = currentUser?.role?.toLowerCase() === 'admin';
  if (!isAdmin) return null;

  const [open,      setOpen]      = useState(false);
  const [fields,    setFields]    = useState(null); // null until loaded
  const [saving,    setSaving]    = useState(false);
  const [saved,     setSaved]     = useState(false);
  const [err,       setErr]       = useState('');
  const [finding,   setFinding]   = useState(false);
  const [findResult, setFindResult] = useState(null); // { found: true, matches: [] } | { found: false }

  const inp = {
    width: '100%', padding: '8px 10px', border: `1px solid ${C.border}`,
    borderRadius: '8px', fontSize: '13px', color: C.charcoal,
    background: C.white, outline: 'none', boxSizing: 'border-box',
  };
  const lbl = { fontSize: '12px', fontWeight: 600, color: C.charcoal, display: 'block', marginBottom: '4px' };

  // Load fresh values from DB when section is opened
  useEffect(() => {
    if (!open || fields !== null) return;
    async function load() {
      const { data, error } = await supabase
        .from('team_members')
        .select('eitje_user_id, leave_accrual_pct, balance_calc_start_date, opening_ot_balance, opening_leave_balance, last_day')
        .eq('id', member.id)
        .single();
      if (!error && data) {
        setFields({
          eitje_user_id:           data.eitje_user_id           ?? '',
          leave_accrual_pct:       data.leave_accrual_pct       ?? 8.0,
          // Default to join_date if balance_calc_start_date not yet set
          balance_calc_start_date: data.balance_calc_start_date || member.join_date || '',
          opening_ot_balance:      data.opening_ot_balance      ?? 0,
          opening_leave_balance:   data.opening_leave_balance   ?? 0,
          last_day:                data.last_day                ?? '',
        });
      }
    }
    load();
  }, [open, member.id]);

  async function handleSave() {
    setErr('');
    setSaving(true);
    const payload = {
      eitje_user_id:           fields.eitje_user_id?.toString().trim() || null,
      leave_accrual_pct:       parseFloat(fields.leave_accrual_pct)    || 8.0,
      balance_calc_start_date: fields.balance_calc_start_date          || null,
      opening_ot_balance:      parseFloat(fields.opening_ot_balance)   || 0,
      opening_leave_balance:   parseFloat(fields.opening_leave_balance) || 0,
      last_day:                fields.last_day                         || null,
    };
    const { error } = await supabase.from('team_members').update(payload).eq('id', member.id);
    setSaving(false);
    if (error) { setErr('Save failed: ' + error.message); return; }
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
    if (onSaved) onSaved(payload);
  }

  function set(k, v) { setFields(p => ({ ...p, [k]: v })); setFindResult(null); }

  async function handleFind() {
    if (!member.name) return;
    setFinding(true);
    setFindResult(null);
    // Normalise: strip accents, lowercase, strip non-alpha
    function norm(s) {
      return (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9 ]/g, '').trim();
    }
    const target = norm(member.name);
    const parts  = target.split(/\s+/).filter(Boolean);

    // Fetch distinct users from eitje_hours
    const { data } = await supabase
      .from('eitje_hours')
      .select('eitje_user_id, user_name, date')
      .not('eitje_user_id', 'is', null)
      .order('date', { ascending: false })
      .limit(15000);

    if (!data) { setFinding(false); setFindResult({ found: false }); return; }

    // Deduplicate by eitje_user_id, keep most recent
    const map = new Map();
    data.forEach(r => {
      if (!r.eitje_user_id) return;
      const k = String(r.eitje_user_id);
      if (!map.has(k)) map.set(k, { eitje_user_id: k, user_name: r.user_name, last_seen: r.date });
    });
    const users = Array.from(map.values());

    // Match attempts: exact → reversed → all parts present → substring
    let hits = users.filter(u => norm(u.user_name) === target);
    if (!hits.length) hits = users.filter(u => norm(u.user_name).split(/\s+/).reverse().join(' ') === target);
    if (!hits.length && parts.length >= 2) hits = users.filter(u => { const n = norm(u.user_name).split(/\s+/); return parts.every(p => n.includes(p)); });
    if (!hits.length && parts[0]?.length >= 3) hits = users.filter(u => parts.every(p => norm(u.user_name).includes(p)));

    hits = hits.sort((a, b) => (b.last_seen || '').localeCompare(a.last_seen || ''));

    if (hits.length === 1) {
      set('eitje_user_id', hits[0].eitje_user_id);
      setFindResult({ found: true, auto: true, match: hits[0] });
    } else if (hits.length > 1) {
      setFindResult({ found: true, auto: false, matches: hits.slice(0, 5) });
    } else {
      setFindResult({ found: false });
    }
    setFinding(false);
  }

  const isFT = member.employment_type === 'Full Time';

  return (
    <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: '16px' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', cursor: 'pointer', padding: 0, width: '100%', textAlign: 'left' }}
      >
        <span style={{ fontSize: '11px', fontWeight: 700, color: C.skyText, textTransform: 'uppercase', letterSpacing: '0.05em', flex: 1 }}>
          Eitje & Balance Settings
        </span>
        {open ? <ChevronUp size={14} color={C.mid} /> : <ChevronDown size={14} color={C.mid} />}
      </button>

      {open && (
        <div style={{ marginTop: '14px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {fields === null ? (
            <div style={{ fontSize: '13px', color: C.mid }}>Loading…</div>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
                {/* Eitje User ID + Find button */}
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={lbl}>Eitje User ID</label>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <input
                      value={fields.eitje_user_id}
                      onChange={e => set('eitje_user_id', e.target.value)}
                      placeholder="e.g. 76370"
                      style={{ ...inp, flex: 1 }}
                    />
                    <button
                      onClick={handleFind}
                      disabled={finding}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '4px',
                        padding: '8px 14px', background: finding ? C.border : C.skyText,
                        color: C.white, border: 'none', borderRadius: '8px',
                        cursor: finding ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap',
                      }}
                    >
                      {finding ? '…' : '🔍 Find'}
                    </button>
                  </div>
                  {findResult?.found && findResult.auto && (
                    <div style={{ marginTop: '6px', padding: '6px 10px', background: C.greenLight, borderRadius: '6px', fontSize: '11px', color: C.green }}>
                      ✓ Auto-matched: <strong>{findResult.match.user_name}</strong> · ID {findResult.match.eitje_user_id} · last shift {findResult.match.last_seen}
                    </div>
                  )}
                  {findResult?.found && !findResult.auto && (
                    <div style={{ marginTop: '6px', padding: '8px 10px', background: C.amberLight, borderRadius: '6px', border: `1px solid ${C.amber}33` }}>
                      <div style={{ fontSize: '11px', color: C.amber, fontWeight: 600, marginBottom: '6px' }}>Multiple matches — pick one:</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {findResult.matches.map(u => (
                          <button key={u.eitje_user_id} onClick={() => { set('eitje_user_id', u.eitje_user_id); setFindResult({ found: true, auto: true, match: u }); }}
                            style={{ textAlign: 'left', padding: '6px 10px', background: C.white, border: `1px solid ${C.border}`, borderRadius: '6px', cursor: 'pointer', fontSize: '12px', color: C.charcoal }}>
                            <strong>{u.user_name}</strong> <span style={{ color: C.mid }}>· last {u.last_seen} · ID {u.eitje_user_id}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {findResult && !findResult.found && (
                    <div style={{ marginTop: '6px', padding: '6px 10px', background: C.redLight, borderRadius: '6px', fontSize: '11px', color: C.red }}>
                      No match found — enter ID manually.
                    </div>
                  )}
                </div>

                {/* Balance Calc Start */}
                <div>
                  <label style={lbl}>Balance Calc Start <span style={{ fontWeight: 400, color: C.mid }}>(if not join date)</span></label>
                  <input
                    type="date"
                    value={fields.balance_calc_start_date}
                    onChange={e => set('balance_calc_start_date', e.target.value)}
                    style={inp}
                  />
                </div>

                {/* Last Day */}
                <div>
                  <label style={lbl}>
                    Last Day <span style={{ fontWeight: 400, color: C.mid }}>(termination cutoff)</span>
                  </label>
                  <input
                    type="date"
                    value={fields.last_day}
                    onChange={e => set('last_day', e.target.value)}
                    style={{ ...inp, borderColor: fields.last_day ? C.amber : C.border }}
                  />
                  {fields.last_day && (
                    <div style={{ marginTop: '5px', fontSize: '11px', color: C.amber, fontWeight: 600 }}>
                      Accrual stops after {new Date(fields.last_day + 'T12:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}. Hours after this date are excluded from recalc.
                    </div>
                  )}
                </div>

                {isFT && (
                  <>
                    <div>
                      <label style={lbl}>Leave Accrual % <span style={{ fontWeight: 400, color: C.mid }}>(default 8.0)</span></label>
                      <input
                        type="number" step="0.01" min="0" max="20"
                        value={fields.leave_accrual_pct}
                        onChange={e => set('leave_accrual_pct', e.target.value)}
                        style={inp}
                      />
                    </div>
                    <div>
                      <label style={lbl}>Opening OT Balance <span style={{ fontWeight: 400, color: C.mid }}>(carry-in, hours)</span></label>
                      <input
                        type="number" step="0.01"
                        value={fields.opening_ot_balance}
                        onChange={e => set('opening_ot_balance', e.target.value)}
                        style={inp}
                      />
                    </div>
                    <div>
                      <label style={lbl}>Opening Leave Balance <span style={{ fontWeight: 400, color: C.mid }}>(carry-in, hours)</span></label>
                      <input
                        type="number" step="0.01"
                        value={fields.opening_leave_balance}
                        onChange={e => set('opening_leave_balance', e.target.value)}
                        style={inp}
                      />
                    </div>
                  </>
                )}
              </div>

              {err && (
                <div style={{ fontSize: '12px', color: C.red, background: C.redLight, borderRadius: '8px', padding: '8px 12px' }}>{err}</div>
              )}

              <div>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  style={{
                    padding: '8px 20px', background: saved ? C.green : saving ? C.border : C.navy,
                    color: C.white, border: 'none', borderRadius: '8px',
                    cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '13px',
                    display: 'flex', alignItems: 'center', gap: '6px',
                  }}
                >
                  {saved ? <><Check size={13} /> Saved</> : saving ? 'Saving…' : 'Save Settings'}
                </button>
                <div style={{ fontSize: '11px', color: C.mid, marginTop: '6px' }}>
                  After saving, re-run the recalc to apply the updated balance settings.
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// MEMBER MODAL
// ══════════════════════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════════════════════
// UNPAID LEAVE SECTION — manages unpaid_leave entries for one member
// ══════════════════════════════════════════════════════════════════════
function UnpaidLeaveSection({ member, currentUser }) {
  const isAdmin = currentUser?.role?.toLowerCase() === 'admin';
  const today   = new Date();
  const [rows, setRows]   = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [year, setYear]   = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [hours, setHours] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr]     = useState('');

  async function load() {
    const { data } = await supabase
      .from('unpaid_leave')
      .select('*')
      .eq('team_member_id', member.id)
      .order('year', { ascending: false })
      .order('month', { ascending: false });
    setRows(data || []);
    setLoaded(true);
  }

  useEffect(() => { load(); }, [member.id]);

  async function save() {
    setErr('');
    const h = parseFloat(hours);
    if (isNaN(h) || h <= 0) { setErr('Hours must be greater than zero'); return; }
    setSaving(true);
    const { error } = await supabase
      .from('unpaid_leave')
      .upsert({
        team_member_id: member.id,
        year, month,
        hours: h,
        reason: reason || null,
        created_by: currentUser?.name || 'Admin',
      }, { onConflict: 'team_member_id,year,month' });
    setSaving(false);
    if (error) { setErr(error.message); return; }
    setHours(''); setReason(''); setShowForm(false);
    await load();
  }

  async function remove(id) {
    if (!window.confirm('Remove this unpaid leave entry?')) return;
    await supabase.from('unpaid_leave').delete().eq('id', id);
    await load();
  }

  const yearOpts  = [today.getFullYear(), today.getFullYear() - 1, today.getFullYear() - 2];
  const monthOpts = [1,2,3,4,5,6,7,8,9,10,11,12];

  return (
    <div>
      <div style={{ fontSize: '11px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>Unpaid Leave</span>
        {isAdmin && (
          <button
            onClick={() => setShowForm(!showForm)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: C.skyText, fontWeight: 600 }}
          >
            {showForm ? 'Cancel' : '+ Add'}
          </button>
        )}
      </div>

      {!loaded ? (
        <div style={{ padding: '12px', background: C.cream, borderRadius: '8px', fontSize: '12px', color: C.mid }}>Loading…</div>
      ) : (
        <>
          {showForm && isAdmin && (
            <div style={{ padding: '12px', background: C.cream, border: `1px solid ${C.border}`, borderRadius: '8px', marginBottom: '10px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                <div>
                  <label style={{ fontSize: '10px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', display: 'block', marginBottom: '3px' }}>Month</label>
                  <select value={month} onChange={e => setMonth(parseInt(e.target.value))} style={{ width: '100%', padding: '6px 8px', border: `1px solid ${C.border}`, borderRadius: '6px', fontSize: '12px', background: C.white }}>
                    {monthOpts.map(m => <option key={m} value={m}>{MONTHS[m-1]}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '10px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', display: 'block', marginBottom: '3px' }}>Year</label>
                  <select value={year} onChange={e => setYear(parseInt(e.target.value))} style={{ width: '100%', padding: '6px 8px', border: `1px solid ${C.border}`, borderRadius: '6px', fontSize: '12px', background: C.white }}>
                    {yearOpts.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '10px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', display: 'block', marginBottom: '3px' }}>Hours</label>
                  <input type="number" step="0.1" min="0" value={hours} onChange={e => setHours(e.target.value)} placeholder="e.g. 38" style={{ width: '100%', padding: '6px 8px', border: `1px solid ${C.border}`, borderRadius: '6px', fontSize: '12px', boxSizing: 'border-box' }}/>
                </div>
              </div>
              <div style={{ marginBottom: '8px' }}>
                <label style={{ fontSize: '10px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', display: 'block', marginBottom: '3px' }}>Reason (optional)</label>
                <input value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Personal time off" style={{ width: '100%', padding: '6px 8px', border: `1px solid ${C.border}`, borderRadius: '6px', fontSize: '12px', boxSizing: 'border-box' }}/>
              </div>
              {err && <div style={{ fontSize: '11px', color: C.red, marginBottom: '8px' }}>{err}</div>}
              <button
                onClick={save}
                disabled={saving}
                style={{ padding: '6px 14px', borderRadius: '6px', border: 'none', background: saving ? C.border : C.green, color: C.white, fontWeight: 600, fontSize: '12px', cursor: saving ? 'not-allowed' : 'pointer' }}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
              <span style={{ fontSize: '11px', color: C.mid, marginLeft: '10px' }}>
                Existing entry for the same month will be overwritten. Re-run the recalc to see updated balances.
              </span>
            </div>
          )}

          {rows.length === 0 ? (
            <div style={{ padding: '12px', background: C.cream, borderRadius: '8px', fontSize: '12px', color: C.mid }}>
              No unpaid leave recorded.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {rows.map(r => (
                <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 12px', background: C.white, border: `1px solid ${C.border}`, borderRadius: '8px' }}>
                  <div style={{ minWidth: '90px', fontSize: '12px', fontWeight: 700, color: C.charcoal }}>
                    {MONTHS[r.month - 1]} {r.year}
                  </div>
                  <div style={{ minWidth: '60px', fontSize: '13px', fontWeight: 700, color: C.amber }}>
                    {r.hours}h
                  </div>
                  <div style={{ flex: 1, fontSize: '12px', color: C.mid, fontStyle: r.reason ? 'normal' : 'italic' }}>
                    {r.reason || '—'}
                  </div>
                  {isAdmin && (
                    <button onClick={() => remove(r.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.red, fontSize: '16px', padding: '0 4px' }} title="Remove">
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function LeaveOTMemberModal({ member: memberProp, currentUser, allEntries, snapshots, onClose, onMemberUpdated, onReloadMembers }) {
  // Always fetch fresh member data from DB on open — prop may be stale
  const [member, setMember] = useState(memberProp);
  useEffect(() => {
    async function loadFresh() {
      const { data } = await supabase.from('team_members').select('*').eq('id', memberProp.id).single();
      if (data) { setMember(data); if (onMemberUpdated) onMemberUpdated(data); }
    }
    loadFresh();
  }, [memberProp.id]);

  // onSettingsSaved — called when Eitje/balance settings are saved inside the modal
  function onSettingsSaved(updatedFields) {
    const updated = { ...member, ...updatedFields };
    setMember(updated);
    if (onMemberUpdated) onMemberUpdated(updated);
    if (onReloadMembers) onReloadMembers();
  }
  const [confirmations, setConfirmations] = useState([]);
  const [sending,       setSending]       = useState(false);
  const [sendDone,      setSendDone]      = useState(false);
  const [sendError,     setSendError]     = useState('');
  const [windowOffset,  setWindowOffset]  = useState(0); // 0 = most recent 12 months

  // Post-termination hours warning
  const [postTermHours, setPostTermHours] = useState(null); // null = not loaded | { count, total, from, to } | { count: 0 }
  useEffect(() => {
    if (!member.last_day || !member.eitje_user_id) { setPostTermHours(null); return; }
    async function checkPostTerm() {
      const eitjeId = parseInt(member.eitje_user_id, 10);
      if (isNaN(eitjeId)) return;
      const { data } = await supabase
        .from('eitje_hours')
        .select('date, hours_worked, type')
        .eq('eitje_user_id', eitjeId)
        .in('type', ['gewerkte_uren', 'verlof', 'ziek', 'bijzonder_verlof'])
        .gt('date', member.last_day)
        .order('date');
      if (!data || data.length === 0) { setPostTermHours({ count: 0 }); return; }
      const total = data.reduce((s, r) => s + (parseFloat(r.hours_worked) || 0), 0);
      setPostTermHours({ count: data.length, total: r1(total), from: data[0].date, to: data[data.length - 1].date });
    }
    checkPostTerm();
  }, [member.id, member.last_day, member.eitje_user_id]);

  const memberEntries = allEntries
    .filter(e => e.team_member_id === member.id)
    .map(e => {
      const snap = snapshots.find(s => s.id === e.snapshot_id);
      return snap ? { ...e, month: snap.month, year: snap.year } : null;
    })
    .filter(Boolean)
    .sort((a, b) => a.year !== b.year ? a.year - b.year : a.month - b.month);

  // Exclude current month from balance cards and line chart — hours are incomplete
  const _now = new Date();
  const nowY = _now.getFullYear();
  const nowM = _now.getMonth() + 1;
  const completedEntries = memberEntries.filter(e => !(e.year === nowY && e.month === nowM));

  // 12-month sliding window — offset 0 = most recent 12
  const WINDOW = 12;
  const totalMonths = completedEntries.length;
  const maxOffset = Math.max(0, totalMonths - WINDOW);
  // clamp offset in case data shrinks
  const clampedOffset = Math.min(windowOffset, maxOffset);
  const windowStart = Math.max(0, totalMonths - WINDOW - clampedOffset);
  const windowEnd   = Math.max(0, totalMonths - clampedOffset);
  const windowedEntries = completedEntries.slice(windowStart, windowEnd);

  // Bar chart uses full memberEntries (includes current month) with same window position
  const barWindowStart = Math.max(0, memberEntries.length - WINDOW - clampedOffset);
  const barWindowEnd   = Math.max(0, memberEntries.length - clampedOffset);
  const barWindowedEntries = memberEntries.slice(barWindowStart, barWindowEnd);

  const chartData = windowedEntries.map(e => ({
    label: `${MONTHS[e.month - 1]} ${String(e.year).slice(2)}`,
    ot:    r1(e.ot_balance),
    leave: r1(e.leave_balance),
  }));

  const latest     = completedEntries[completedEntries.length - 1];
  const prevEntry  = completedEntries[completedEntries.length - 2];
  const latestSnap = snapshots.find(s => s.id === latest?.snapshot_id);
  const otDiff     = latest && prevEntry ? r1(parseFloat(latest.ot_balance) - parseFloat(prevEntry.ot_balance)) : null;
  const leaveDiff  = latest && prevEntry ? r1(parseFloat(latest.leave_balance) - parseFloat(prevEntry.leave_balance)) : null;

  useEffect(() => {
    async function loadConfirmations() {
      const { data } = await supabase
        .from('leave_confirmations').select('*')
        .eq('team_member_id', member.id)
        .order('sent_at', { ascending: false });
      if (data) setConfirmations(data);
    }
    loadConfirmations();
  }, [member.id]);

  async function handleSendLink() {
    if (!member.email) { setSendError('No email address on file for this team member.'); return; }
    setSending(true); setSendError('');
    const token      = genToken();
    const balanceUrl = `${window.location.origin}/balance/${token}`;

    const { error: dbErr } = await supabase.from('leave_confirmations').insert({
      token, team_member_id: member.id, sent_by: currentUser?.name || 'Manager',
      snapshot_month: latestSnap?.month ?? null, snapshot_year: latestSnap?.year ?? null,
      ot_balance: latest?.ot_balance ?? null, leave_balance: latest?.leave_balance ?? null,
    });
    if (dbErr) { setSendError('Failed to create link: ' + dbErr.message); setSending(false); return; }

    const { error: fnErr } = await supabase.functions.invoke('send-balance-email', {
      body: {
        to: member.email, name: member.name, balanceUrl,
        sentBy: currentUser?.name || 'Manager',
        otBalance: latest?.ot_balance ?? '—',
        leaveBalance: latest?.leave_balance ?? '—',
        month: latestSnap ? `${MONTHS[latestSnap.month - 1]} ${latestSnap.year}` : '',
      },
    });
    if (fnErr) setSendError('Email failed — but the link was created. Share manually if needed.');

    const { data: updated } = await supabase
      .from('leave_confirmations').select('*')
      .eq('team_member_id', member.id).order('sent_at', { ascending: false });
    if (updated) setConfirmations(updated);

    setSendDone(true); setSending(false);
    setTimeout(() => setSendDone(false), 3000);
  }

  async function handleExportCsv() {
    const eitjeId = parseInt(member.eitje_user_id, 10);
    if (isNaN(eitjeId)) { alert('No Eitje ID on file for this member.'); return; }

    const effStart = member.balance_calc_start_date;
    if (!effStart) { alert('No start date on file for this member.'); return; }

    const { data, error } = await supabase
      .from('eitje_hours')
      .select('date, type, hours_worked, approved')
      .eq('eitje_user_id', eitjeId)
      .in('type', ['gewerkte_uren', 'verlof', 'ziek', 'bijzonder_verlof'])
      .gte('date', effStart)
      .order('date');

    if (error) { alert('Failed to fetch hours: ' + error.message); return; }
    if (!data || data.length === 0) { alert('No hours data found.'); return; }

    const TYPE_LABELS = {
      gewerkte_uren:    'Worked',
      verlof:           'Leave',
      ziek:             'Sick',
      bijzonder_verlof: 'Special Leave',
    };

    const rows = [
      ['Date', 'Type', 'Hours', 'Approved'],
      ...data.map(r => [
        r.date,
        TYPE_LABELS[r.type] || r.type,
        parseFloat(r.hours_worked).toFixed(2),
        r.approved ? 'Yes' : 'No',
      ])
    ];

    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `${member.name.replace(/\s+/g, '_')}_hours.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const hasData = chartData.length > 0;

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: C.white, borderRadius: '16px', width: '100%', maxWidth: '700px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,0.22)' }}>

        {/* Header */}
        <div style={{ padding: '20px 24px', background: C.navy, borderRadius: '16px 16px 0 0', display: 'flex', alignItems: 'center', gap: '14px', position: 'sticky', top: 0, zIndex: 10 }}>
          <RoleIcon dept={member.dept} size={44} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: '17px', color: C.white }}>{member.name}</div>
            <div style={{ fontSize: '13px', color: C.skyBlue, marginTop: '2px' }}>
              {member.dept} · {member.venue}
              {member.email && <span style={{ marginLeft: '8px', opacity: 0.7 }}>· {member.email}</span>}
            </div>
          </div>
          {member.contract_hours && (
            <span style={{ padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 700, background: 'rgba(255,255,255,0.12)', color: C.skyBlue }}>
              {member.contract_hours}hr/wk
            </span>
          )}
          {member.last_day && (
            <span style={{ padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 700, background: C.amberLight, color: C.amber, border: `1px solid ${C.amber}55` }}>
              Leaving {new Date(member.last_day + 'T12:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
            </span>
          )}
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: '8px', cursor: 'pointer', color: C.white, padding: '6px', display: 'flex' }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '24px' }}>

          {/* Post-termination hours warning */}
          {postTermHours && postTermHours.count > 0 && (
            <div style={{ background: C.amberLight, border: `1px solid ${C.amber}66`, borderRadius: '12px', padding: '14px 18px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <AlertTriangle size={18} color={C.amber} style={{ flexShrink: 0, marginTop: '1px' }} />
              <div>
                <div style={{ fontSize: '13px', fontWeight: 700, color: C.amber, marginBottom: '4px' }}>
                  Hours recorded after termination date
                </div>
                <div style={{ fontSize: '13px', color: C.charcoal }}>
                  <strong>{postTermHours.count} shift{postTermHours.count === 1 ? '' : 's'}</strong>, totalling <strong>{postTermHours.total}h</strong>, recorded between{' '}
                  <strong>{new Date(postTermHours.from + 'T12:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</strong>
                  {postTermHours.from !== postTermHours.to && (
                    <> – <strong>{new Date(postTermHours.to + 'T12:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</strong></>
                  )}{postTermHours.from === postTermHours.to && (
                    <> <strong>{new Date(postTermHours.from + 'T12:00:00').toLocaleDateString('en-GB', { year: 'numeric' })}</strong></>
                  )}{' '}— after last day of{' '}
                  <strong>{new Date(member.last_day + 'T12:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</strong>.
                </div>
                <div style={{ fontSize: '11px', color: C.mid, marginTop: '5px' }}>
                  These hours are excluded from the balance recalc. Verify before processing final payroll.
                </div>
              </div>
            </div>
          )}

          {/* Current balances */}
          {hasData ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
              {[
                { label: 'OT Balance',    value: latest?.ot_balance    },
                { label: 'Leave Balance', value: latest?.leave_balance },
                { label: 'Combined',      value: latest ? r1(parseFloat(latest.ot_balance) + parseFloat(latest.leave_balance)) : null },
              ].map(({ label, value }) => {
                const n = value !== null && value !== undefined ? parseFloat(value) : null;
                const bg = n === null ? C.cream : n > 0 ? C.skyBlueLight : n < 0 ? C.redLight : C.cream;
                return (
                <div key={label} style={{ background: bg, borderRadius: '12px', padding: '16px 20px', border: `1px solid ${C.border}` }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>{label}</div>
                  <div style={{ fontSize: '20px', fontWeight: 700, color: C.charcoal }}>
                    {n !== null ? `${n}h` : '—'}
                  </div>
                  {latestSnap && (
                    <div style={{ fontSize: '11px', color: C.mid, marginTop: '6px' }}>
                      As of {MONTHS[latestSnap.month - 1]} {latestSnap.year}
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          ) : (
            <div style={{ padding: '32px', textAlign: 'center', background: C.cream, borderRadius: '12px', color: C.mid, fontSize: '13px' }}>
              No balance data yet for this team member
            </div>
          )}

          {/* Balance trend line chart */}
          {hasData && chartData.length > 1 && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                <div style={{ fontSize: '11px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Balance History
                </div>
                {totalMonths > WINDOW && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <button
                      onClick={() => setWindowOffset(o => Math.min(o + WINDOW, maxOffset))}
                      disabled={clampedOffset >= maxOffset}
                      style={{ padding: '3px 10px', borderRadius: '6px', border: `1px solid ${C.border}`, background: clampedOffset >= maxOffset ? C.cream : C.white, color: clampedOffset >= maxOffset ? C.border : C.charcoal, cursor: clampedOffset >= maxOffset ? 'default' : 'pointer', fontSize: '13px', fontWeight: 700 }}
                    >‹</button>
                    <span style={{ fontSize: '11px', color: C.mid, minWidth: '60px', textAlign: 'center' }}>
                      {windowStart + 1}–{windowEnd} of {totalMonths}
                    </span>
                    <button
                      onClick={() => setWindowOffset(o => Math.max(o - WINDOW, 0))}
                      disabled={clampedOffset === 0}
                      style={{ padding: '3px 10px', borderRadius: '6px', border: `1px solid ${C.border}`, background: clampedOffset === 0 ? C.cream : C.white, color: clampedOffset === 0 ? C.border : C.charcoal, cursor: clampedOffset === 0 ? 'default' : 'pointer', fontSize: '13px', fontWeight: 700 }}
                    >›</button>
                  </div>
                )}
              </div>
              <div style={{ background: C.cream, borderRadius: '12px', padding: '16px', border: `1px solid ${C.border}` }}>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={chartData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: C.mid }} />
                    <YAxis tick={{ fontSize: 11, fill: C.mid }} tickFormatter={v => `${v}h`} />
                    <Tooltip content={<ChartTooltip />} />
                    <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '8px' }} />
                    <ReferenceLine y={0} stroke={C.red} strokeDasharray="4 4" strokeOpacity={0.5} />
                    <Line type="monotone" dataKey="ot"    name="OT Balance"    stroke={C.amber}   strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} connectNulls />
                    <Line type="monotone" dataKey="leave" name="Leave Balance" stroke={C.skyText} strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Hours breakdown stacked bar chart */}
          {hasData && barWindowedEntries.length > 0 && (
            <HoursBreakdownChart memberEntries={barWindowedEntries} />
          )}

          {/* Send balance link — admin only */}
          {currentUser?.role?.toLowerCase() === 'admin' && (
          <div style={{ background: C.skyBlueLight, borderRadius: '12px', padding: '16px 20px', border: `1px solid ${C.skyBlue}44` }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: C.skyText, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
              Send Balance Link
            </div>
            <div style={{ fontSize: '13px', color: C.charcoal, marginBottom: '12px' }}>
              Sends {member.name} a personal link to view their balance history. When they open it, it is automatically logged below.
            </div>
            {sendError && (
              <div style={{ fontSize: '12px', color: C.amber, background: C.amberLight, borderRadius: '8px', padding: '8px 12px', marginBottom: '10px' }}>
                {sendError}
              </div>
            )}
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button
                onClick={handleSendLink}
                disabled={sending || !hasData}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px', padding: '9px 18px',
                  background: sendDone ? C.green : (sending || !hasData) ? C.border : C.navy,
                  color: C.white, border: 'none', borderRadius: '8px',
                  cursor: (sending || !hasData) ? 'not-allowed' : 'pointer',
                  fontWeight: 600, fontSize: '13px', transition: 'background 0.2s',
                }}
              >
                {sendDone ? <><CheckCircle size={14} /> Sent!</> : sending ? 'Sending…' : <><Send size={14} /> Send Balance Link</>}
              </button>
              <button
                onClick={handleExportCsv}
                disabled={!member.eitje_user_id}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px', padding: '9px 18px',
                  background: !member.eitje_user_id ? C.border : C.greenLight,
                  color: !member.eitje_user_id ? C.white : C.green,
                  border: `1px solid ${C.green}44`, borderRadius: '8px',
                  cursor: !member.eitje_user_id ? 'not-allowed' : 'pointer',
                  fontWeight: 600, fontSize: '13px',
                }}
              >
                ↓ Export Hours CSV
              </button>
            </div>
            {!member.email && <div style={{ fontSize: '11px', color: C.amber, marginTop: '6px' }}>No email on file — add it in the Team Members tab first.</div>}
            {!hasData    && <div style={{ fontSize: '11px', color: C.mid,   marginTop: '6px' }}>No balance data to send yet.</div>}
          </div>
          )}{/* end admin-only send balance */}

          {/* Unpaid leave management */}
          <UnpaidLeaveSection member={member} currentUser={currentUser} />

          {/* Eitje & Balance Settings — admin only, collapsible */}
          <EitjeBalanceSettings member={member} currentUser={currentUser} onSaved={onSettingsSaved} />

          {/* Audit log */}
          {confirmations.length > 0 && (
            <div>
              <div style={{ fontSize: '11px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px' }}>
                Send History
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {confirmations.map(c => (
                  <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px', background: C.white, borderRadius: '8px', border: `1px solid ${C.border}` }}>
                    <div style={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0, background: c.viewed_at ? C.greenLight : C.cream, display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px solid ${c.viewed_at ? C.green + '44' : C.border}` }}>
                      {c.viewed_at ? <Eye size={14} color={C.green} /> : <EyeOff size={14} color={C.mid} />}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: C.charcoal }}>
                        Sent by {c.sent_by}
                        {c.snapshot_month && c.snapshot_year && (
                          <span style={{ fontWeight: 400, color: C.mid }}> · {MONTHS[c.snapshot_month - 1]} {c.snapshot_year} balances</span>
                        )}
                      </div>
                      <div style={{ fontSize: '11px', color: C.mid, marginTop: '2px' }}>{fmtDT(c.sent_at)}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      {c.viewed_at ? (
                        <>
                          <div style={{ fontSize: '11px', fontWeight: 700, color: C.green }}>Viewed</div>
                          <div style={{ fontSize: '11px', color: C.mid }}>{fmtDT(c.viewed_at)}</div>
                        </>
                      ) : (
                        <span style={{ fontSize: '11px', color: C.mid, fontStyle: 'italic' }}>Not yet viewed</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// RECALCULATION ENGINE
// ══════════════════════════════════════════════════════════════════════

// Constant: weeks per year used in monthly conversion (matches Excel: 38 × 52.14/12 = 165.12)
const WEEKS_PER_YEAR = 52.14;

// Eitje shift types that count toward total hours for OT calc
const COUNTED_TYPES = ['gewerkte_uren', 'verlof', 'ziek', 'bijzonder_verlof'];

// Format a Date as YYYY-MM-DD
function isoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Days in a given month (1=Jan, 12=Dec)
function daysInMonth(year, month) {
  return new Date(year, month, 0).getDate();
}

// Get effective_start as a Date — ONLY uses balance_calc_start_date.
// If not set, member is skipped from calc entirely.
function effectiveStartDate(member) {
  const s = member.balance_calc_start_date;
  if (!s) return null;
  const [y, mo, d] = s.split('-').map(Number);
  return new Date(y, mo - 1, d);
}

// For a given (year, month) and member, compute pro-ration factor (1.0 = full month)
// Accounts for both a mid-month start (effectiveStart) and a mid-month termination (lastDay).
function proRationFactor(year, month, effectiveStart, lastDay) {
  const monthStart = new Date(year, month - 1, 1);
  const monthEnd   = new Date(year, month, 0);
  const dim        = daysInMonth(year, month);

  // Determine active range within this month
  let activeFrom = monthStart;
  let activeTo   = monthEnd;

  if (effectiveStart) {
    if (effectiveStart > monthEnd)  return 0.0; // not started yet
    if (effectiveStart > monthStart) activeFrom = effectiveStart;
  }

  if (lastDay) {
    if (lastDay < monthStart) return 0.0; // already left before this month
    if (lastDay < monthEnd)   activeTo = lastDay;
  }

  const daysActive = activeTo.getDate() - activeFrom.getDate() + 1;
  return daysActive / dim;
}

/**
 * Compute one month's leave/OT entry for one member.
 * Returns null if member should be skipped (effective_start in the future, or no eitje_user_id).
 *
 * Pure function — takes pre-fetched eitje rows for the member, returns a result object.
 * No DB calls. Caller is responsible for fetching rows and persisting results.
 *
 * @param {Object} member         team_members row
 * @param {Array}  memberHoursMonth eitje_hours rows for this member, this month, already filtered by counted types
 * @param {number} year           e.g. 2026
 * @param {number} month          1-12
 * @param {number} priorOtCum     cumulative OT delta from member's start through end of prior month
 * @param {number} priorLeaveCum  cumulative leave delta from member's start through end of prior month
 * @param {number} unpaidHours    hours of unpaid leave for this member this month (reduces contract & accrual)
 * @returns {Object|null}         { ot_balance, leave_balance, breakdown, skipped, skipReason, ... }
 */
function computeMonthEntry(member, memberHoursMonth, year, month, priorOtCum, priorLeaveCum, unpaidHours) {
  if (!member.eitje_user_id) return { skipped: true, skipReason: 'No Eitje user ID' };

  const effStart = effectiveStartDate(member);
  if (!effStart) return { skipped: true, skipReason: 'No join date or balance_calc_start_date' };

  // Parse last_day if set
  const lastDayRaw = member.last_day;
  let lastDay = null;
  if (lastDayRaw) {
    const [ly, lm, ld] = lastDayRaw.split('-').map(Number);
    lastDay = new Date(ly, lm - 1, ld);
  }

  const factor = proRationFactor(year, month, effStart, lastDay);
  if (factor === 0) {
    const monthStart = new Date(year, month - 1, 1);
    if (lastDay && lastDay < monthStart) return { skipped: true, skipReason: `Last day (${lastDayRaw}) is before this month` };
    return { skipped: true, skipReason: `Effective start (${isoDate(effStart)}) is after this month` };
  }

  const contract_hours      = parseFloat(member.contract_hours) || 0;
  const accrual_pct         = parseFloat(member.leave_accrual_pct);
  const accrual_pct_clean   = isNaN(accrual_pct) ? 8.0 : accrual_pct;
  const unpaid              = parseFloat(unpaidHours) || 0;

  const contract_monthly_full = contract_hours * WEEKS_PER_YEAR / 12;
  const accrual_monthly_full  = contract_hours * (accrual_pct_clean / 100) * WEEKS_PER_YEAR / 12;

  // Pro-rate for first month, then reduce by unpaid leave (unpaid hrs neither owed as work nor accruing leave)
  const contract_target_pre_unpaid = contract_monthly_full * factor;
  const accrual_target_pre_unpaid  = accrual_monthly_full  * factor;
  const contract_target = Math.max(0, contract_target_pre_unpaid - unpaid);
  const accrual_target  = Math.max(0, accrual_target_pre_unpaid  - unpaid * (accrual_pct_clean / 100));

  // Sum hours by type — only rows on/after effective_start and on/before last_day (if set)
  const effStartIso = isoDate(effStart);
  const lastDayIso  = lastDay ? isoDate(lastDay) : null;
  let worked = 0, verlof = 0, ziek = 0, special = 0;
  for (const r of memberHoursMonth) {
    if (r.date < effStartIso) continue; // exclude shifts before effective start
    if (lastDayIso && r.date > lastDayIso) continue; // exclude shifts after termination
    const hrs = parseFloat(r.hours_worked) || 0;
    if      (r.type === 'gewerkte_uren')    worked  += hrs;
    else if (r.type === 'verlof')           verlof  += hrs;
    else if (r.type === 'ziek')             ziek    += hrs;
    else if (r.type === 'bijzonder_verlof') special += hrs;
  }
  const total = worked + verlof + ziek + special;

  // DIAGNOSTIC — remove after debugging
  if (String(member.eitje_user_id) === '31221' && year === 2026 && month === 1) {
    console.log('[DIAG] Enrico Jan 2026 — rows:', memberHoursMonth.length, '| worked:', worked, '| verlof:', verlof, '| ziek:', ziek, '| total:', total);
    console.log('[DIAG] Enrico Jan 2026 — row dates:', memberHoursMonth.map(r => `${r.date}:${r.type}:${r.hours_worked}`).join(', '));
  }

  const ot_delta    = total - contract_target;
  const leave_delta = accrual_target - verlof;

  const ot_balance    = (priorOtCum    || 0) + ot_delta;
  const leave_balance = (priorLeaveCum || 0) + leave_delta;

  // Notes string — preserves keys used by HoursBreakdownChart (worked/leave/sick/special/contract/ot_delta)
  // Adds: accrual, leave_delta, lv_delta (compat with Excel data), prorate, unpaid
  const notesParts = [
    `worked=${r1(worked)}`,
    `leave=${r1(verlof)}`,
    `sick=${r1(ziek)}`,
    `special=${r1(special)}`,
    `contract=${r1(contract_target)}`,
    `ot_delta=${r1(ot_delta)}`,
    `accrual=${r1(accrual_target)}`,
    `leave_delta=${r1(leave_delta)}`,
    `lv_delta=${r1(leave_delta)}`,
    `prorate=${factor.toFixed(3)}`,
  ];
  if (unpaid > 0) notesParts.push(`unpaid=${r1(unpaid)}`);
  const notes = notesParts.join(';');

  return {
    skipped: false,
    ot_balance:    r1(ot_balance),
    leave_balance: r1(leave_balance),
    ot_delta:      r1(ot_delta),
    leave_delta:   r1(leave_delta),
    contract_target: r1(contract_target),
    accrual_target:  r1(accrual_target),
    total, worked, verlof, ziek, special,
    unpaid,
    factor,
    notes,
  };
}

/**
 * Run the recalc for ALL FT members for a given month.
 * This computes each member's running balance from their effective_start through the calc month
 * by iterating through every month in between and accumulating deltas.
 *
 * @param {Array}  ftMembers      list of FT, active team_members rows with eitje_user_id populated
 * @param {Array}  allMemberHours all eitje_hours rows from earliest member start through end of calc month
 *                                Must be filtered to only counted types to keep this fast.
 * @param {Array}  allUnpaidLeave all unpaid_leave rows for the relevant members in the relevant period
 * @param {number} calcYear
 * @param {number} calcMonth      1-12
 * @returns {Array} list of {member, result, monthlyHistory, missingMonths}
 *                 missingMonths = [{year, month}] for months between effective_start and calc month with NO Eitje rows
 */
function recalcForMonth(ftMembers, allMemberHours, allUnpaidLeave, calcYear, calcMonth) {
  // Index hours by eitje_user_id → year-month → array of rows
  const byUserMonth = new Map();
  for (const row of allMemberHours) {
    if (!row.eitje_user_id || !row.date) continue;
    if (!COUNTED_TYPES.includes(row.type)) continue;
    // Parse date string directly to avoid UTC→local timezone shift misassigning months
    const [ry, rm] = row.date.split('-').map(Number);
    const ym = `${ry}-${rm}`;
    const key = `${parseInt(row.eitje_user_id, 10)}|${ym}`;
    if (!byUserMonth.has(key)) byUserMonth.set(key, []);
    byUserMonth.get(key).push(row);
  }

  // Index unpaid leave by team_member_id → year-month → hours
  const unpaidByMemberMonth = new Map();
  for (const u of (allUnpaidLeave || [])) {
    if (!u.team_member_id || u.year == null || u.month == null) continue;
    const key = `${u.team_member_id}|${u.year}-${u.month}`;
    unpaidByMemberMonth.set(key, parseFloat(u.hours) || 0);
  }

  const results = [];
  for (const member of ftMembers) {
    const effStart = effectiveStartDate(member);
    if (!effStart) {
      results.push({ member, result: { skipped: true, skipReason: 'No Balance Calc Start date set — add one in the Hours tab member modal' }, missingMonths: [], monthlyHistory: [] });
      continue;
    }
    const calcMonthEnd = new Date(calcYear, calcMonth, 0);
    if (effStart > calcMonthEnd) {
      results.push({ member, result: { skipped: true, skipReason: `Effective start (${isoDate(effStart)}) is after the calc month` }, missingMonths: [], monthlyHistory: [] });
      continue;
    }
    if (!member.eitje_user_id) {
      results.push({ member, result: { skipped: true, skipReason: 'No Eitje user ID' }, missingMonths: [], monthlyHistory: [] });
      continue;
    }

    // Initialise cumulative balances from opening balances on the member record
    let otCum    = parseFloat(member.opening_ot_balance)    || 0;
    let leaveCum = parseFloat(member.opening_leave_balance) || 0;

    // Parse last_day for loop cutoff
    let memberLastDay = null;
    if (member.last_day) {
      const [ly, lm, ld] = member.last_day.split('-').map(Number);
      memberLastDay = new Date(ly, lm - 1, ld);
    }

    const monthlyHistory = [];
    const missingMonths  = [];
    const startY = effStart.getFullYear();
    const startM = effStart.getMonth() + 1;

    let y = startY, m = startM;
    while (y < calcYear || (y === calcYear && m <= calcMonth)) {
      // Stop loop if we've gone past the member's last day
      if (memberLastDay) {
        const monthStart = new Date(y, m - 1, 1);
        if (monthStart > memberLastDay) break;
      }
      const eitjeKey  = `${parseInt(member.eitje_user_id, 10)}|${y}-${m}`;
      const monthRows = byUserMonth.get(eitjeKey) || [];
      const unpaidKey = `${member.id}|${y}-${m}`;
      const unpaid    = unpaidByMemberMonth.get(unpaidKey) || 0;

      // Track missing months (no Eitje rows at all for this user/month)
      if (monthRows.length === 0) {
        missingMonths.push({ year: y, month: m });
      }
      const r = computeMonthEntry(member, monthRows, y, m, otCum, leaveCum, unpaid);
      if (!r.skipped) {
        otCum    = r.ot_balance;
        leaveCum = r.leave_balance;
        monthlyHistory.push({ year: y, month: m, ...r });
      }
      // advance one month
      m++;
      if (m > 12) { m = 1; y++; }
    }

    // Final entry = calc month's record
    const finalEntry = monthlyHistory[monthlyHistory.length - 1];
    if (!finalEntry) {
      results.push({ member, result: { skipped: true, skipReason: 'No months to calculate' }, missingMonths, monthlyHistory: [] });
    } else {
      results.push({ member, result: finalEntry, monthlyHistory, missingMonths });
    }
  }

  return results;
}

// ══════════════════════════════════════════════════════════════════════
// RECALCULATE PANEL — UI for triggering month recalc with preview
// ══════════════════════════════════════════════════════════════════════
function RecalculatePanel({ members, currentUser, onComplete }) {
  const isAdmin = currentUser?.role?.toLowerCase() === 'admin';
  if (!isAdmin) return null;

  const today = new Date();
  const [expanded, setExpanded] = useState(false);
  // Default to previous completed month — current month hours are incomplete
  const prevMonth = today.getMonth() === 0 ? 12 : today.getMonth();
  const prevYear  = today.getMonth() === 0 ? today.getFullYear() - 1 : today.getFullYear();
  const [year,     setYear]     = useState(prevYear);
  const [month,    setMonth]    = useState(prevMonth);
  const [fullHistory, setFullHistory] = useState(false); // true = recompute every month from earliest start through selected
  const [preview,  setPreview]  = useState(null); // null | { results, eitjeRowCount }
  const [loading,  setLoading]  = useState(false);
  const [committing, setCommitting] = useState(false);
  const [committed,  setCommitted]  = useState(null); // { snapshotIds: [{year,month,id}], count }
  const [error,    setError]    = useState(null);
  const [lastRun,  setLastRun]  = useState(null);
  const [override, setOverride] = useState(false); // allow commit despite missing-month coverage

  // Load last run info
  useEffect(() => {
    async function loadLast() {
      const { data } = await supabase
        .from('eitje_sync_log')
        .select('*')
        .eq('resource', 'leave_ot')
        .eq('status', 'success')
        .order('completed_at', { ascending: false })
        .limit(1);
      if (data?.[0]) setLastRun(data[0]);
    }
    loadLast();
  }, [committed]);

  const yearOpts  = [today.getFullYear(), today.getFullYear() - 1, today.getFullYear() - 2];
  const monthOpts = [1,2,3,4,5,6,7,8,9,10,11,12];

  // Active FT members with eitje_user_id required for calc
  const ftActive    = members.filter(m => m.employment_type === 'Full Time' && m.active !== false);
  const ftReady     = ftActive.filter(m => m.eitje_user_id);
  const ftMissingId = ftActive.filter(m => !m.eitje_user_id);

  async function runPreview() {
    setLoading(true);
    setError(null);
    setCommitted(null);
    setPreview(null);

    try {
      const calcMonthEnd = new Date(year, month, 0);
      const toIso = isoDate(calcMonthEnd);

      // One query per member, scoped to their own date range only.
      // No pagination needed — each member's rows are a manageable set.
      // Postgres returns raw rows; JS does the SUMIF grouping per member.
      // This eliminates the cross-member bulk fetch, the pagination ordering bug,
      // and the eitje_user_id type mismatch that caused silent empty results.
      let allRows = [];
      let totalRawRows = 0;

      await Promise.all(ftReady.map(async member => {
        const eitjeId = parseInt(member.eitje_user_id, 10);
        if (isNaN(eitjeId)) return;

        const effStart = effectiveStartDate(member);
        if (!effStart || effStart > calcMonthEnd) return;
        const fromIso = isoDate(effStart);

        const { data, error: e } = await supabase
          .from('eitje_hours')
          .select('date, type, hours_worked')
          .eq('eitje_user_id', eitjeId)
          .in('type', COUNTED_TYPES)
          .gte('date', fromIso)
          .lte('date', toIso);

        if (e) throw new Error(`Hours query failed for ${member.name}: ${e.message}`);
        if (!data) return;

        totalRawRows += data.length;

        // Tag each row with the eitje_user_id (integer) so recalcForMonth can index it correctly
        for (const row of data) {
          allRows.push({ ...row, eitje_user_id: eitjeId });
        }
      }));

      // Load unpaid leave entries for relevant FT members
      const memberIds = ftReady.map(m => m.id);
      let unpaidRows = [];
      if (memberIds.length > 0) {
        const { data: u, error: ue } = await supabase
          .from('unpaid_leave')
          .select('team_member_id, year, month, hours')
          .in('team_member_id', memberIds);
        if (ue) throw new Error('Unpaid leave query failed: ' + ue.message);
        unpaidRows = u || [];
      }

      const results = recalcForMonth(ftReady, allRows, unpaidRows, year, month);

      // Add the missing-ID members so they're shown in the preview
      for (const m of ftMissingId) {
        results.push({ member: m, result: { skipped: true, skipReason: 'No Eitje user ID — set in Team page' }, missingMonths: [], monthlyHistory: [] });
      }

      setPreview({ results, eitjeRowCount: totalRawRows, unpaidRowCount: unpaidRows.length });
      setOverride(false); // reset override on each new preview
    } catch (e) {
      setError(e.message || 'Preview failed');
    } finally {
      setLoading(false);
    }
  }

  async function commit() {
    if (!preview) return;
    setCommitting(true);
    setError(null);

    const logId = crypto.randomUUID();
    await supabase.from('eitje_sync_log').insert({
      id: logId,
      resource: 'leave_ot',
      trigger_type: fullHistory ? 'full_history' : 'manual',
      start_date: `${year}-${String(month).padStart(2,'0')}-01`,
      end_date:   isoDate(new Date(year, month, 0)),
      status: 'running',
    });

    try {
      let totalEntries = 0;
      const writtenSnapshots = [];

      if (fullHistory) {
        // Full-history mode: gather every distinct (year, month) appearing in any member's monthlyHistory
        // Across all processed members, write one snapshot per month + entries for every member who has data for that month
        const monthSet = new Map(); // 'YYYY-M' → { year, month }
        const entriesByMonth = new Map(); // 'YYYY-M' → array of entries

        for (const { member, result, monthlyHistory } of preview.results) {
          if (result.skipped) continue;
          for (const h of (monthlyHistory || [])) {
            const ymKey = `${h.year}-${h.month}`;
            if (!monthSet.has(ymKey)) monthSet.set(ymKey, { year: h.year, month: h.month });
            if (!entriesByMonth.has(ymKey)) entriesByMonth.set(ymKey, []);
            entriesByMonth.get(ymKey).push({
              team_member_id:  member.id,
              name:            member.name,
              venue:           member.venue,
              employment_type: member.employment_type,
              contract_hours:  member.contract_hours,
              ot_balance:      h.ot_balance,
              leave_balance:   h.leave_balance,
              notes:           h.notes,
            });
          }
        }

        // Sort months chronologically
        const sortedYms = Array.from(monthSet.values()).sort((a, b) => a.year !== b.year ? a.year - b.year : a.month - b.month);

        for (const { year: y, month: m } of sortedYms) {
          // Find or create snapshot for this month
          const { data: existing } = await supabase
            .from('leave_ot_snapshots')
            .select('id')
            .eq('year', y).eq('month', m)
            .maybeSingle();

          let snapshotId;
          if (existing?.id) {
            snapshotId = existing.id;
            const { error: delErr } = await supabase
              .from('leave_ot_entries')
              .delete()
              .eq('snapshot_id', snapshotId);
            if (delErr) throw new Error(`Failed to clear entries for ${y}-${m}: ${delErr.message}`);
          } else {
            const { data: newSnap, error: insErr } = await supabase
              .from('leave_ot_snapshots')
              .insert({ year: y, month: m, uploaded_by: `${currentUser?.name || 'Admin'} (full-history recalc)` })
              .select()
              .single();
            if (insErr) throw new Error(`Failed to create snapshot for ${y}-${m}: ${insErr.message}`);
            snapshotId = newSnap.id;
          }

          // Insert entries for this month
          const monthEntries = (entriesByMonth.get(`${y}-${m}`) || []).map(e => ({ ...e, snapshot_id: snapshotId }));
          if (monthEntries.length > 0) {
            const { error: insErr } = await supabase
              .from('leave_ot_entries')
              .insert(monthEntries);
            if (insErr) throw new Error(`Failed to write entries for ${y}-${m}: ${insErr.message}`);
            totalEntries += monthEntries.length;
          }
          writtenSnapshots.push({ year: y, month: m, id: snapshotId, count: monthEntries.length });
        }
      } else {
        // Single-month mode (original behaviour)
        const { data: existing } = await supabase
          .from('leave_ot_snapshots')
          .select('id')
          .eq('year', year)
          .eq('month', month)
          .maybeSingle();

        let snapshotId;
        if (existing?.id) {
          snapshotId = existing.id;
          const { error: delErr } = await supabase
            .from('leave_ot_entries')
            .delete()
            .eq('snapshot_id', snapshotId);
          if (delErr) throw new Error('Failed to clear existing entries: ' + delErr.message);
        } else {
          const { data: newSnap, error: insErr } = await supabase
            .from('leave_ot_snapshots')
            .insert({
              year, month,
              uploaded_by: `${currentUser?.name || 'Admin'} (auto-recalc)`,
            })
            .select()
            .single();
          if (insErr) throw new Error('Failed to create snapshot: ' + insErr.message);
          snapshotId = newSnap.id;
        }

        const entries = [];
        for (const { member, result } of preview.results) {
          if (result.skipped) continue;
          entries.push({
            snapshot_id:     snapshotId,
            team_member_id:  member.id,
            name:            member.name,
            venue:           member.venue,
            employment_type: member.employment_type,
            contract_hours:  member.contract_hours,
            ot_balance:      result.ot_balance,
            leave_balance:   result.leave_balance,
            notes:           result.notes,
          });
        }

        if (entries.length > 0) {
          const { error: insErr } = await supabase
            .from('leave_ot_entries')
            .insert(entries);
          if (insErr) throw new Error('Failed to write entries: ' + insErr.message);
        }
        totalEntries = entries.length;
        writtenSnapshots.push({ year, month, id: snapshotId, count: entries.length });
      }

      await supabase.from('eitje_sync_log')
        .update({ status: 'success', records_synced: totalEntries, completed_at: new Date().toISOString() })
        .eq('id', logId);

      setCommitted({ snapshots: writtenSnapshots, count: totalEntries });
      setPreview(null);
      if (onComplete) onComplete();
    } catch (e) {
      await supabase.from('eitje_sync_log')
        .update({ status: 'error', error_msg: e.message, completed_at: new Date().toISOString() })
        .eq('id', logId);
      setError(e.message || 'Commit failed');
    } finally {
      setCommitting(false);
    }
  }

  const previewSkipped   = preview?.results.filter(r => r.result.skipped) || [];
  const previewProcessed = preview?.results.filter(r => !r.result.skipped) || [];
  const previewZeroHrs   = previewProcessed.filter(r => r.result.total === 0);
  // Coverage: count members with at least one missing month and total missing months across processed members
  const membersWithMissing = previewProcessed.filter(r => (r.missingMonths?.length || 0) > 0);
  const totalMissingMonths = previewProcessed.reduce((s, r) => s + (r.missingMonths?.length || 0), 0);
  const hasCoverageGap     = totalMissingMonths > 0;
  const canCommit          = previewProcessed.length > 0 && (!hasCoverageGap || override);

  return (
    <div style={{ background: C.white, borderRadius: '12px', border: `1px solid ${C.border}`, marginBottom: '20px', overflow: 'hidden' }}>
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          width: '100%', padding: '14px 18px', background: 'none', border: 'none',
          display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <Calculator size={16} color={C.skyText}/>
        <span style={{ fontWeight: 700, fontSize: '14px', color: C.charcoal, flex: 1 }}>
          Recalculate Balances from Eitje
        </span>
        {lastRun && (
          <span style={{ fontSize: '11px', color: C.mid, marginRight: '8px' }}>
            Last run: {new Date(lastRun.completed_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </span>
        )}
        <span style={{ padding: '3px 10px', borderRadius: '20px', background: C.amberLight, color: C.amber, fontSize: '11px', fontWeight: 700 }}>ADMIN</span>
        {expanded ? <ChevronUp size={16} color={C.mid}/> : <ChevronDown size={16} color={C.mid}/>}
      </button>

      {expanded && (
        <div style={{ padding: '0 18px 18px 18px', borderTop: `1px solid ${C.border}` }}>

          {/* Description */}
          <div style={{ fontSize: '12px', color: C.mid, marginTop: '12px', marginBottom: '14px', lineHeight: 1.5 }}>
            Computes OT and leave balances from Eitje hours data for all active FT staff. Running balances start from each member's <code style={{ background: C.cream, padding: '1px 4px', borderRadius: '4px' }}>opening_ot_balance</code> + <code style={{ background: C.cream, padding: '1px 4px', borderRadius: '4px' }}>opening_leave_balance</code>, then accumulate deltas from their effective start through the selected month. Unpaid leave reduces that month's contract & accrual targets. <strong>Always preview before committing</strong>.
          </div>

          {/* Month/Year picker + mode toggle */}
          <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', marginBottom: '8px', flexWrap: 'wrap' }}>
            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: C.mid, marginBottom: '4px', textTransform: 'uppercase' }}>Month</label>
              <select value={month} onChange={e => { setMonth(parseInt(e.target.value)); setPreview(null); setCommitted(null); }}
                style={{ padding: '8px 10px', border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', background: C.white }}>
                {monthOpts.map(m => <option key={m} value={m}>{MONTHS[m-1]}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: C.mid, marginBottom: '4px', textTransform: 'uppercase' }}>Year</label>
              <select value={year} onChange={e => { setYear(parseInt(e.target.value)); setPreview(null); setCommitted(null); }}
                style={{ padding: '8px 10px', border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', background: C.white }}>
                {yearOpts.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <button
              onClick={runPreview}
              disabled={loading || committing}
              style={{
                padding: '8px 18px', borderRadius: '8px', border: 'none',
                background: (loading || committing) ? C.border : C.skyText,
                color: C.white, fontWeight: 700, fontSize: '13px',
                cursor: (loading || committing) ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', gap: '6px',
              }}
            >
              <RefreshCw size={13} style={loading ? { animation: 'spin 1s linear infinite' } : {}}/>
              {loading ? 'Loading…' : 'Preview'}
            </button>
          </div>

          {/* Full history toggle */}
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: C.charcoal, marginBottom: '14px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={fullHistory}
              onChange={e => { setFullHistory(e.target.checked); setPreview(null); setCommitted(null); }}
            />
            <span>
              <strong>Full-history recalc</strong> — replace snapshots for <em>every month</em> from each member's effective start through {MONTHS[month-1]} {year}. Use this for one-time backfill or major data correction. Otherwise leave unchecked to recalc just the selected month.
            </span>
          </label>

          {error && (
            <div style={{ background: C.redLight, border: `1px solid ${C.red}44`, borderRadius: '8px', padding: '10px 14px', marginBottom: '14px', color: C.red, fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <AlertTriangle size={14}/> {error}
            </div>
          )}

          {committed && (
            <div style={{ background: C.greenLight, border: `1px solid ${C.green}44`, borderRadius: '8px', padding: '12px 14px', marginBottom: '14px', color: C.green, fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Check size={14}/>
              {committed.snapshots?.length === 1
                ? <>Committed {committed.count} entries for {MONTHS[committed.snapshots[0].month-1]} {committed.snapshots[0].year}.</>
                : <>Committed {committed.count} entries across {committed.snapshots?.length || 0} snapshots.</>}
            </div>
          )}

          {/* Preview results */}
          {preview && (
            <div>
              {/* Summary cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '10px', marginBottom: '14px' }}>
                {[
                  { label: 'Will Process',     value: previewProcessed.length, color: C.green },
                  { label: 'Skipped',          value: previewSkipped.length,   color: C.mid },
                  { label: 'Zero-hour Months', value: previewZeroHrs.length,   color: previewZeroHrs.length > 0 ? C.amber : C.mid },
                  { label: 'Missing Months',   value: totalMissingMonths,      color: hasCoverageGap ? C.red : C.green },
                  { label: 'Eitje Rows',       value: preview.eitjeRowCount,   color: C.skyText },
                ].map(s => (
                  <div key={s.label} style={{ background: C.cream, borderRadius: '8px', padding: '10px 12px', border: `1px solid ${C.border}` }}>
                    <div style={{ fontSize: '10px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', marginBottom: '4px' }}>{s.label}</div>
                    <div style={{ fontSize: '18px', fontWeight: 700, color: s.color }}>{s.value}</div>
                  </div>
                ))}
              </div>

              {/* Coverage gap warning */}
              {hasCoverageGap && (
                <div style={{ marginBottom: '14px', background: C.redLight, border: `1px solid ${C.red}55`, borderRadius: '8px', padding: '12px 14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                    <AlertTriangle size={14} color={C.red}/>
                    <span style={{ fontSize: '13px', fontWeight: 700, color: C.red }}>
                      Coverage gap: {membersWithMissing.length} member{membersWithMissing.length === 1 ? '' : 's'} missing {totalMissingMonths} month{totalMissingMonths === 1 ? '' : 's'} of Eitje data
                    </span>
                  </div>
                  <div style={{ fontSize: '12px', color: C.charcoal, marginBottom: '8px' }}>
                    These members have months between their effective start and the calc month with no Eitje rows at all. The recalc will treat those months as zero-hours, producing a wildly incorrect running balance. Use Bulk Hours Sync (DataSync page) to backfill, or check the override below.
                  </div>
                  <div style={{ maxHeight: '140px', overflowY: 'auto', background: C.white, borderRadius: '6px', padding: '6px 10px', border: `1px solid ${C.border}` }}>
                    {membersWithMissing.map(({ member, missingMonths }) => (
                      <div key={member.id} style={{ fontSize: '12px', padding: '3px 0', color: C.charcoal }}>
                        <strong>{member.name}</strong>
                        <span style={{ color: C.mid }}> — missing {missingMonths.length} month{missingMonths.length === 1 ? '' : 's'}: </span>
                        <span style={{ color: C.red }}>{missingMonths.map(m => `${MONTHS[m.month-1]} ${String(m.year).slice(2)}`).join(', ')}</span>
                      </div>
                    ))}
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '10px', fontSize: '12px', color: C.charcoal, cursor: 'pointer' }}>
                    <input type="checkbox" checked={override} onChange={e => setOverride(e.target.checked)}/>
                    <span>I understand the balances will be inaccurate — commit anyway</span>
                  </label>
                </div>
              )}

              {/* Skipped list */}
              {previewSkipped.length > 0 && (
                <div style={{ marginBottom: '14px' }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', marginBottom: '6px' }}>
                    Skipped ({previewSkipped.length})
                  </div>
                  <div style={{ background: C.cream, borderRadius: '8px', padding: '8px 12px', border: `1px solid ${C.border}`, maxHeight: '120px', overflowY: 'auto' }}>
                    {previewSkipped.map(({ member, result }) => (
                      <div key={member.id} style={{ fontSize: '12px', color: C.charcoal, padding: '3px 0' }}>
                        <span style={{ fontWeight: 600 }}>{member.name}</span>
                        <span style={{ color: C.mid }}> — {result.skipReason}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Processed table */}
              {previewProcessed.length > 0 && (
                <div style={{ marginBottom: '14px' }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', marginBottom: '6px' }}>
                    Will Write ({previewProcessed.length}) — for {MONTHS[month-1]} {year}
                  </div>
                  <div style={{ border: `1px solid ${C.border}`, borderRadius: '8px', overflow: 'hidden', maxHeight: '320px', overflowY: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                      <thead>
                        <tr style={{ background: C.cream, borderBottom: `1px solid ${C.border}` }}>
                          {['Name','Venue','Total','Worked','Verlof','Ziek','Spec','Contract','OT Δ','OT Bal','Lv Δ','Lv Bal','Miss'].map(h => (
                            <th key={h} style={{ padding: '6px 8px', textAlign: h === 'Name' || h === 'Venue' ? 'left' : 'right', fontSize: '10px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {previewProcessed.map(({ member, result, missingMonths }, i) => {
                          const zeroFlag = result.total === 0;
                          const missCount = missingMonths?.length || 0;
                          return (
                            <tr key={member.id} style={{ borderBottom: `1px solid ${C.border}`, background: i % 2 === 0 ? C.white : C.cream }}>
                              <td style={{ padding: '5px 8px', fontWeight: 600, color: C.charcoal, whiteSpace: 'nowrap' }}>
                                {zeroFlag && <AlertTriangle size={10} color={C.amber} style={{ display: 'inline', marginRight: '4px' }}/>}
                                {member.name}
                              </td>
                              <td style={{ padding: '5px 8px', color: C.mid, whiteSpace: 'nowrap' }}>{(member.venue||'').replace('Box ','')}</td>
                              <td style={{ padding: '5px 8px', textAlign: 'right' }}>{r1(result.total)}</td>
                              <td style={{ padding: '5px 8px', textAlign: 'right', color: C.mid }}>{r1(result.worked)}</td>
                              <td style={{ padding: '5px 8px', textAlign: 'right', color: C.mid }}>{r1(result.verlof)}</td>
                              <td style={{ padding: '5px 8px', textAlign: 'right', color: C.mid }}>{r1(result.ziek)}</td>
                              <td style={{ padding: '5px 8px', textAlign: 'right', color: C.mid }}>{r1(result.special)}</td>
                              <td style={{ padding: '5px 8px', textAlign: 'right' }}>{result.contract_target}</td>
                              <td style={{ padding: '5px 8px', textAlign: 'right', color: result.ot_delta >= 0 ? C.green : C.red, fontWeight: 600 }}>{result.ot_delta >= 0 ? '+' : ''}{result.ot_delta}</td>
                              <td style={{ padding: '5px 8px', textAlign: 'right', fontWeight: 700 }}>{result.ot_balance}</td>
                              <td style={{ padding: '5px 8px', textAlign: 'right', color: result.leave_delta >= 0 ? C.green : C.red, fontWeight: 600 }}>{result.leave_delta >= 0 ? '+' : ''}{result.leave_delta}</td>
                              <td style={{ padding: '5px 8px', textAlign: 'right', fontWeight: 700 }}>{result.leave_balance}</td>
                              <td style={{ padding: '5px 8px', textAlign: 'right', color: missCount > 0 ? C.red : C.mid, fontWeight: missCount > 0 ? 700 : 400 }}>{missCount}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {previewZeroHrs.length > 0 && (
                    <div style={{ fontSize: '11px', color: C.amber, marginTop: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <AlertTriangle size={11}/> {previewZeroHrs.length} member{previewZeroHrs.length === 1 ? '' : 's'} with zero hours this month — verify Eitje ID mapping is correct
                    </div>
                  )}
                </div>
              )}

              {/* Commit button */}
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', paddingTop: '10px', borderTop: `1px solid ${C.border}` }}>
                <button
                  onClick={commit}
                  disabled={committing || !canCommit}
                  title={!canCommit && hasCoverageGap ? 'Resolve coverage gap or check the override box' : ''}
                  style={{
                    padding: '9px 22px', borderRadius: '8px', border: 'none',
                    background: (committing || !canCommit) ? C.border : (hasCoverageGap ? C.amber : C.green),
                    color: C.white, fontWeight: 700, fontSize: '13px',
                    cursor: (committing || !canCommit) ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', gap: '6px',
                  }}
                >
                  <Check size={13}/>
                  {committing
                    ? 'Committing…'
                    : fullHistory
                      ? `Commit Full History (through ${MONTHS[month-1]} ${year})`
                      : hasCoverageGap
                        ? `Commit Anyway (${MONTHS[month-1]} ${year})`
                        : `Commit ${MONTHS[month-1]} ${year} Snapshot`}
                </button>
                <button
                  onClick={() => { setPreview(null); setOverride(false); }}
                  disabled={committing}
                  style={{ padding: '9px 16px', borderRadius: '8px', border: `1px solid ${C.border}`, background: C.white, color: C.mid, cursor: committing ? 'not-allowed' : 'pointer', fontSize: '13px' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// PT MEMBER MODAL
// ══════════════════════════════════════════════════════════════════════
function PTMemberModal({ member, currentUser, onClose }) {
  const [chartData, setChartData] = useState([]);
  const [loading,   setLoading]   = useState(true);

  useEffect(() => {
    async function load() {
      const eitjeId = parseInt(member.eitje_user_id, 10);
      if (isNaN(eitjeId)) { setLoading(false); return; }

      const startDate = member.balance_calc_start_date || member.join_date;
      if (!startDate) { setLoading(false); return; }

      // Fetch all hours since start date
      let allRows = [];
      const PAGE = 2000;
      let from = 0;
      while (true) {
        const { data, error } = await supabase
          .from('eitje_hours')
          .select('date, type, hours_worked')
          .eq('eitje_user_id', eitjeId)
          .in('type', ['gewerkte_uren', 'verlof', 'ziek', 'bijzonder_verlof'])
          .gte('date', startDate)
          .order('date')
          .range(from, from + PAGE - 1);
        if (error || !data || data.length === 0) break;
        allRows = allRows.concat(data);
        if (data.length < PAGE) break;
        from += PAGE;
      }

      // Aggregate by year-month
      const byMonth = {};
      allRows.forEach(r => {
        const [y, m] = r.date.split('-').map(Number);
        const key = `${y}-${String(m).padStart(2,'0')}`;
        if (!byMonth[key]) byMonth[key] = { year: y, month: m, worked: 0, leave: 0, sick: 0, special: 0 };
        const hrs = parseFloat(r.hours_worked) || 0;
        if      (r.type === 'gewerkte_uren')    byMonth[key].worked  += hrs;
        else if (r.type === 'verlof')           byMonth[key].leave   += hrs;
        else if (r.type === 'ziek')             byMonth[key].sick    += hrs;
        else if (r.type === 'bijzonder_verlof') byMonth[key].special += hrs;
      });

      const sorted = Object.values(byMonth).sort((a,b) => a.year !== b.year ? a.year - b.year : a.month - b.month);
      setChartData(sorted.map(e => ({
        ...e,
        worked:  r1(e.worked),
        leave:   r1(e.leave),
        sick:    r1(e.sick),
        special: r1(e.special),
        label:   `${MONTHS[e.month - 1]} ${String(e.year).slice(2)}`,
      })));
      setLoading(false);
    }
    load();
  }, [member.id]);

  function handleExportCsv() {
    if (!chartData.length) { alert('No data to export.'); return; }
    const rows = [
      ['Month', 'Worked', 'Leave', 'Sick', 'Special', 'Total'],
      ...chartData.map(d => [
        `${MONTHS[d.month-1]} ${d.year}`,
        d.worked.toFixed(2),
        d.leave.toFixed(2),
        d.sick.toFixed(2),
        d.special.toFixed(2),
        r1(d.worked + d.leave + d.sick + d.special).toFixed(2),
      ])
    ];
    const csv  = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `${member.name.replace(/\s+/g,'_')}_hours.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const BAR_COLORS = { worked: '#4A7B8C', leave: '#A8D5B5', sick: '#E07B00', special: '#F4B8C1' };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:600, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background:C.white, borderRadius:'16px', width:'100%', maxWidth:'700px', maxHeight:'90vh', overflowY:'auto', boxShadow:'0 24px 64px rgba(0,0,0,0.22)' }}>

        {/* Header */}
        <div style={{ padding:'20px 24px', background:C.navy, borderRadius:'16px 16px 0 0', display:'flex', alignItems:'center', gap:'14px', position:'sticky', top:0, zIndex:10 }}>
          <RoleIcon dept={member.dept} size={44} />
          <div style={{ flex:1 }}>
            <div style={{ fontWeight:700, fontSize:'17px', color:C.white }}>{member.name}</div>
            <div style={{ fontSize:'13px', color:C.skyBlue, marginTop:'2px' }}>
              PT · {member.dept} · {member.venue}
              {member.email && <span style={{ marginLeft:'8px', opacity:0.7 }}>· {member.email}</span>}
            </div>
          </div>
          <button
            onClick={handleExportCsv}
            style={{ display:'flex', alignItems:'center', gap:'5px', padding:'7px 14px', background:'rgba(255,255,255,0.12)', border:'1px solid rgba(255,255,255,0.2)', borderRadius:'8px', cursor:'pointer', color:C.white, fontSize:'12px', fontWeight:600 }}
          >
            ↓ CSV
          </button>
          <button onClick={onClose} style={{ background:'rgba(255,255,255,0.12)', border:'none', borderRadius:'8px', cursor:'pointer', color:C.white, padding:'6px', display:'flex' }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ padding:'24px', display:'flex', flexDirection:'column', gap:'20px' }}>
          {loading ? (
            <div style={{ padding:'40px', textAlign:'center', color:C.mid, fontSize:'13px' }}>Loading hours…</div>
          ) : chartData.length === 0 ? (
            <div style={{ padding:'40px', textAlign:'center', color:C.mid, fontSize:'13px', background:C.cream, borderRadius:'12px' }}>
              No hours data found{!member.eitje_user_id ? ' — no Eitje ID set on this TM card' : ''}
            </div>
          ) : (
            <>
              <div style={{ fontSize:'11px', fontWeight:700, color:C.mid, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'-8px' }}>
                Monthly Hours — all time ({chartData.length} months)
              </div>
              {/* Legend */}
              <div style={{ display:'flex', gap:'14px', flexWrap:'wrap' }}>
                {[['worked','Worked'],['leave','Leave'],['sick','Sick'],['special','Special Leave']].map(([k,l]) => (
                  <div key={k} style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'11px', color:C.mid }}>
                    <span style={{ width:10, height:10, borderRadius:'2px', background:BAR_COLORS[k], display:'inline-block' }}/>
                    {l}
                  </div>
                ))}
              </div>
              <div style={{ background:C.cream, borderRadius:'12px', padding:'16px', border:`1px solid ${C.border}` }}>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={chartData} margin={{ top:4, right:8, left:-20, bottom:0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize:10, fill:C.mid }} interval={chartData.length > 18 ? Math.floor(chartData.length / 12) : 0} />
                    <YAxis tick={{ fontSize:11, fill:C.mid }} tickFormatter={v => `${v}h`} />
                    <Tooltip content={<BarTooltip />} />
                    <Bar dataKey="worked"  name="Worked"        stackId="a" fill={BAR_COLORS.worked}  radius={[0,0,0,0]} />
                    <Bar dataKey="leave"   name="Leave"         stackId="a" fill={BAR_COLORS.leave}   radius={[0,0,0,0]} />
                    <Bar dataKey="sick"    name="Sick"          stackId="a" fill={BAR_COLORS.sick}    radius={[0,0,0,0]} />
                    <Bar dataKey="special" name="Special Leave" stackId="a" fill={BAR_COLORS.special} radius={[4,4,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Summary table — last 6 months */}
              <div>
                <div style={{ fontSize:'11px', fontWeight:700, color:C.mid, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'8px' }}>
                  Recent Months
                </div>
                <div style={{ background:C.white, borderRadius:'10px', border:`1px solid ${C.border}`, overflow:'hidden' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px' }}>
                    <thead>
                      <tr style={{ background:C.cream, borderBottom:`1px solid ${C.border}` }}>
                        {['Month','Worked','Leave','Sick','Special','Total'].map(h => (
                          <th key={h} style={{ padding:'8px 12px', textAlign: h==='Month'?'left':'right', fontWeight:700, color:C.mid, textTransform:'uppercase', fontSize:'10px' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[...chartData].reverse().slice(0, 6).map((d, i) => (
                        <tr key={d.label} style={{ borderBottom:`1px solid ${C.border}`, background: i%2===0?C.white:C.cream }}>
                          <td style={{ padding:'8px 12px', fontWeight:600, color:C.charcoal }}>{MONTHS[d.month-1]} {d.year}</td>
                          <td style={{ padding:'8px 12px', textAlign:'right', color:C.charcoal }}>{d.worked}h</td>
                          <td style={{ padding:'8px 12px', textAlign:'right', color:C.mid }}>{d.leave > 0 ? `${d.leave}h` : '—'}</td>
                          <td style={{ padding:'8px 12px', textAlign:'right', color:C.mid }}>{d.sick > 0 ? `${d.sick}h` : '—'}</td>
                          <td style={{ padding:'8px 12px', textAlign:'right', color:C.mid }}>{d.special > 0 ? `${d.special}h` : '—'}</td>
                          <td style={{ padding:'8px 12px', textAlign:'right', fontWeight:700, color:C.charcoal }}>{r1(d.worked+d.leave+d.sick+d.special)}h</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <EitjeBalanceSettings member={member} currentUser={currentUser} onSaved={() => {}} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// MAIN
// ══════════════════════════════════════════════════════════════════════
export default function LeaveOT({ members, currentUser, onReloadMembers }) {
  const [snaps,   setSnaps]   = useState([]);
  const [entries, setEntries] = useState([]);
  const [selSnap, setSelSnap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [vf,      setVf]      = useState('All');
  const [df,      setDf]      = useState('All');
  const [etf,     setEtf]     = useState('Full Time'); // employment type filter: Full Time | Part Time | All
  const [sel,     setSel]     = useState(null);
  const [ptSel,   setPtSel]   = useState(null); // PT member modal
  const [ptHours,      setPtHours]      = useState({}); // { team_member_id: { worked, verlof, ziek, special, total } } for selected snapshot's month
  const [ptRollingAvg, setPtRollingAvg] = useState({}); // { team_member_id: number | null } — last-4-week avg hrs/wk

  async function loadSnapshots() {
    const [{ data: s }, { data: e }] = await Promise.all([
      supabase.from('leave_ot_snapshots').select('*').order('year', { ascending: false }).order('month', { ascending: false }),
      supabase.from('leave_ot_entries').select('*'),
    ]);
    if (s) {
      setSnaps(s);
      // Keep existing selection if it still exists, otherwise use latest
      setSelSnap(prev => {
        if (prev && s.find(x => x.id === prev)) return prev;
        // Default to latest completed month (exclude current month)
        const now = new Date();
        const nowY = now.getFullYear();
        const nowM = now.getMonth() + 1;
        const completed = s.filter(x => !(x.year === nowY && x.month === nowM));
        return completed.length > 0 ? completed[0].id : s.length > 0 ? s[0].id : null;
      });
    }
    if (e) setEntries(e || []);
  }

  useEffect(() => {
    async function load() {
      await loadSnapshots();
      setLoading(false);
    }
    load();
  }, []);

  // Lazy-load PT hours from eitje_hours when PT view is active and snapshot changes
  const curSnapForPt = snaps.find(s => s.id === selSnap);
  useEffect(() => {
    if (etf !== 'Part Time' || !curSnapForPt) return;
    async function loadPtHours() {
      const ptMembers = members.filter(m => m.employment_type === 'Part Time' && m.eitje_user_id && m.active !== false);
      if (ptMembers.length === 0) { setPtHours({}); return; }

      const m = curSnapForPt.month, y = curSnapForPt.year;
      const monthStart = `${y}-${String(m).padStart(2,'0')}-01`;
      const monthEnd   = `${y}-${String(m).padStart(2,'0')}-${String(new Date(y, m, 0).getDate()).padStart(2,'0')}`;

      const userIds = ptMembers.map(p => parseInt(p.eitje_user_id, 10)).filter(n => !isNaN(n));
      let allRows = [];
      const PAGE = 1000;
      let from = 0;
      while (true) {
        const { data, error: e } = await supabase
          .from('eitje_hours')
          .select('eitje_user_id, type, hours_worked')
          .in('eitje_user_id', userIds)
          .in('type', COUNTED_TYPES)
          .gte('date', monthStart)
          .lte('date', monthEnd)
          .range(from, from + PAGE - 1);
        if (e || !data || data.length === 0) break;
        allRows = allRows.concat(data);
        if (data.length < PAGE) break;
        from += PAGE;
      }

      // Aggregate by member id
      const agg = {};
      for (const member of ptMembers) {
        agg[member.id] = { worked: 0, verlof: 0, ziek: 0, special: 0, total: 0 };
      }
      for (const r of allRows) {
        const member = ptMembers.find(p => String(p.eitje_user_id) === String(r.eitje_user_id));
        if (!member) continue;
        const hrs = parseFloat(r.hours_worked) || 0;
        if      (r.type === 'gewerkte_uren')    agg[member.id].worked  += hrs;
        else if (r.type === 'verlof')           agg[member.id].verlof  += hrs;
        else if (r.type === 'ziek')             agg[member.id].ziek    += hrs;
        else if (r.type === 'bijzonder_verlof') agg[member.id].special += hrs;
        agg[member.id].total += hrs;
      }
      setPtHours(agg);
    }
    loadPtHours();
  }, [etf, selSnap, members.length, curSnapForPt?.id]);

  // Batch-load last-4-week rolling avg for all PT members when PT view is active
  useEffect(() => {
    if (etf !== 'Part Time') return;
    async function loadPtRolling() {
      const ptMembers = members.filter(m => m.employment_type === 'Part Time' && m.eitje_user_id && m.active !== false);
      if (ptMembers.length === 0) { setPtRollingAvg({}); return; }

      // Same window logic as MemberModal: last completed Sunday → 28 days back
      const today = new Date();
      today.setHours(12, 0, 0, 0);
      const daysToLastSun = today.getDay() === 0 ? 7 : today.getDay();
      const toDate = new Date(today);
      toDate.setDate(today.getDate() - daysToLastSun);
      const fromDate = new Date(toDate);
      fromDate.setDate(toDate.getDate() - 27);
      function toLocalISO(d) {
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
      }
      const fromIso = toLocalISO(fromDate);
      const toIso   = toLocalISO(toDate);

      const userIds = ptMembers.map(p => parseInt(p.eitje_user_id, 10)).filter(n => !isNaN(n));

      const { data } = await supabase
        .from('eitje_hours')
        .select('eitje_user_id, date, type, hours_worked')
        .in('eitje_user_id', userIds)
        .in('type', ['gewerkte_uren', 'verlof', 'ziek', 'bijzonder_verlof'])
        .gte('date', fromIso)
        .lte('date', toIso);

      if (!data) { setPtRollingAvg({}); return; }

      function weekKey(dateStr) {
        const [y, m, day] = dateStr.split('-').map(Number);
        const d = new Date(y, m - 1, day);
        const dow = d.getDay() === 0 ? 6 : d.getDay() - 1;
        return toLocalISO(new Date(y, m - 1, day - dow));
      }

      // Build per-member totals
      const memberMap = {};
      for (const m of ptMembers) {
        memberMap[String(m.eitje_user_id)] = { memberId: m.id, total: 0, weeks: new Set() };
      }
      for (const row of data) {
        const hrs = parseFloat(row.hours_worked) || 0;
        if (hrs <= 0) continue;
        const entry = memberMap[String(row.eitje_user_id)];
        if (!entry) continue;
        entry.total += hrs;
        entry.weeks.add(weekKey(row.date));
      }

      const result = {};
      for (const entry of Object.values(memberMap)) {
        const wc = entry.weeks.size;
        result[entry.memberId] = wc > 0 ? Math.round((entry.total / wc) * 10) / 10 : null;
      }
      setPtRollingAvg(result);
    }
    loadPtRolling();
  }, [etf, members.length]);

  // Apply employment-type filter
  const visibleMembers = members.filter(m => {
    if (etf === 'Full Time' && m.employment_type !== 'Full Time') return false;
    if (etf === 'Part Time' && m.employment_type !== 'Part Time') return false;
    if (m.active === false) return false;
    return true;
  });
  const filt = visibleMembers.filter(m => {
    if (vf !== 'All' && m.venue !== vf) return false;
    if (df !== 'All' && m.dept !== df)  return false;
    return true;
  });

  // For PT view, sort by 4-week avg hours descending (nulls last)
  const filtSorted = etf === 'Part Time'
    ? [...filt].sort((a, b) => {
        const aAvg = ptRollingAvg[a.id] ?? -1;
        const bAvg = ptRollingAvg[b.id] ?? -1;
        return bAvg - aAvg;
      })
    : filt;

  const snapE = entries.filter(e => e.snapshot_id === selSnap);
  const byM   = {};
  snapE.forEach(e => { byM[e.team_member_id] = e; });

  const curSnap   = snaps.find(s => s.id === selSnap);
  const snapIdx   = snaps.findIndex(s => s.id === selSnap);
  const prevSnap  = snapIdx >= 0 && snapIdx < snaps.length - 1 ? snaps[snapIdx + 1] : null;
  const prevSnapE = prevSnap ? entries.filter(e => e.snapshot_id === prevSnap.id) : [];
  const byMPrev   = {};
  prevSnapE.forEach(e => { byMPrev[e.team_member_id] = e; });

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: C.mid }}>Loading…</div>;

  return (
    <div>

      {/* Recalculate panel — admin only, collapsible */}
      <RecalculatePanel members={members} currentUser={currentUser} onComplete={loadSnapshots} />

      {/* Venue trend chart — only shown when FT is in view */}
      {snaps.length > 0 && etf !== 'Part Time' && (
        <VenueTrendChart members={members} entries={entries} snaps={snaps} />
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '16px', alignItems: 'center' }}>
        {['Full Time', 'Part Time', 'All'].map(t => (
          <button key={t} onClick={() => setEtf(t)} style={pill(etf === t, true)}>
            {t === 'All' ? 'All Staff' : t}
          </button>
        ))}
        <div style={{ width: '1px', background: C.border, height: '18px', margin: '0 4px' }} />
        {['All', ...VENUES].map(v => (
          <button key={v} onClick={() => setVf(v)} style={pill(vf === v, true)}>
            {v === 'All' ? 'All Venues' : v.replace('Box ', '')}
          </button>
        ))}
        <div style={{ width: '1px', background: C.border, height: '18px', margin: '0 4px' }} />
        {['All', ...DEPTS].map(d => (
          <button key={d} onClick={() => setDf(d)} style={pill(df === d, true)}>
            {d === 'All' ? 'All Depts' : d}
          </button>
        ))}
      </div>

      {/* Table */}
      {snaps.length === 0 ? (
        <div style={{ background: C.white, borderRadius: '10px', border: `1px solid ${C.border}`, padding: '48px', textAlign: 'center', color: C.mid }}>
          No snapshots uploaded yet
        </div>
      ) : etf === 'Part Time' ? (
        // ── PART-TIME VIEW: hours-worked breakdown for the selected snapshot's month ──
        <div style={{ background: C.white, borderRadius: '10px', border: `1px solid ${C.border}`, overflow: 'hidden' }}>
          <div style={{ padding: '10px 14px', background: C.skyBlueLight, fontSize: '12px', color: C.skyText, borderBottom: `1px solid ${C.border}` }}>
            Showing PT staff hours for <strong>{curSnapForPt ? `${MONTHS[curSnapForPt.month-1]} ${curSnapForPt.year}` : '—'}</strong> (live from Eitje data — not from snapshot). PT staff have no leave/OT balance — leave is paid out monthly.
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: C.cream, borderBottom: `1px solid ${C.border}` }}>
                {[
                  { h: 'Name',    align: 'left'  },
                  { h: 'Venue',   align: 'left'  },
                  { h: 'Dept',    align: 'left'  },
                  { h: 'Avg / wk', align: 'right' },
                  { h: 'Worked',  align: 'right' },
                  { h: 'Verlof',  align: 'right' },
                  { h: 'Ziek',    align: 'right' },
                  { h: 'Special', align: 'right' },
                  { h: 'Total',   align: 'right' },
                ].map(({ h, align }) => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: align, fontSize: '11px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filt.length === 0 ? (
                <tr><td colSpan={9} style={{ padding: '32px', textAlign: 'center', color: C.mid }}>No PT staff match this filter</td></tr>
              ) : filtSorted.map((member, i) => {
                const h = ptHours[member.id];
                const noEitje = !member.eitje_user_id;
                const avg = ptRollingAvg[member.id];
                return (
                  <tr key={member.id}
                    onClick={() => !noEitje && setPtSel(member)}
                    style={{ borderBottom: `1px solid ${C.border}`, background: i % 2 === 0 ? C.white : C.cream, cursor: noEitje ? 'default' : 'pointer' }}
                    onMouseEnter={ev => { if (!noEitje) ev.currentTarget.style.background = C.skyBlueLight; }}
                    onMouseLeave={ev => ev.currentTarget.style.background = i % 2 === 0 ? C.white : C.cream}
                  >
                    <td style={{ padding: '10px 14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <RoleIcon dept={member.dept} size={28} />
                        <span style={{ fontWeight: 600, fontSize: '13px', color: C.charcoal }}>{member.name}</span>
                        {noEitje && <span title="No Eitje user ID" style={{ fontSize: '10px', color: C.amber, fontWeight: 600 }}>(no Eitje ID)</span>}
                      </div>
                    </td>
                    <td style={{ padding: '10px 14px', fontSize: '13px', color: C.mid }}>{member.venue}</td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{ background: C.cream, color: C.mid, padding: '2px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: 600 }}>
                        {member.dept}
                      </span>
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                      {avg != null
                        ? <span style={{ background: C.skyBlueLight, color: C.skyText, padding: '2px 8px', borderRadius: '8px', fontSize: '12px', fontWeight: 700, border: `1px solid ${C.skyBlue}` }}>{avg}h</span>
                        : <span style={{ color: C.border }}>—</span>}
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', fontSize: '13px', color: C.charcoal, fontWeight: 600 }}>
                      {h ? `${r1(h.worked)}h` : (noEitje ? '—' : '0h')}
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', fontSize: '13px', color: C.mid }}>
                      {h ? `${r1(h.verlof)}h` : '—'}
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', fontSize: '13px', color: C.mid }}>
                      {h ? `${r1(h.ziek)}h` : '—'}
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', fontSize: '13px', color: C.mid }}>
                      {h ? `${r1(h.special)}h` : '—'}
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', fontSize: '14px', color: C.charcoal, fontWeight: 700 }}>
                      {h ? `${r1(h.total)}h` : (noEitje ? '—' : '0h')}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        // ── FT or All view: balance table (existing behaviour) ──
        <div style={{ background: C.white, borderRadius: '10px', border: `1px solid ${C.border}`, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: C.cream, borderBottom: `1px solid ${C.border}` }}>
                {[
                  { h: 'Name',          align: 'left'   },
                  { h: 'Venue',         align: 'left'   },
                  { h: 'Dept',          align: 'left'   },
                  { h: 'Type',          align: 'left'   },
                  { h: 'Contract',      align: 'left'   },
                  { h: 'OT Balance',    align: 'right'  },
                  { h: 'Leave Balance', align: 'right'  },
                  { h: 'Combined',      align: 'right'  },
                  { h: 'Link',          align: 'center' },
                ].map(({ h, align }) => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: align, fontSize: '11px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filt.length === 0 ? (
                <tr><td colSpan={9} style={{ padding: '32px', textAlign: 'center', color: C.mid }}>No staff match this filter</td></tr>
              ) : filt.map((member, i) => {
                const isFT = member.employment_type === 'Full Time';
                const e    = byM[member.id];
                const prev = byMPrev[member.id];
                const ot   = (isFT && e?.ot_balance    != null) ? r1(e.ot_balance)    : null;
                const lv   = (isFT && e?.leave_balance != null) ? r1(e.leave_balance) : null;
                const tot  = ot !== null && lv !== null ? r1(ot + lv) : null;

                function Movement({ cur, old }) {
                  if (cur === null || old === null || old === undefined) return null;
                  const diff = r1(cur - parseFloat(old));
                  if (Math.abs(diff) < 0.1) return <span style={{ fontSize: '10px', color: C.border }}>—</span>;
                  const up = diff > 0;
                  return (
                    <span style={{ fontSize: '10px', fontWeight: 600, color: up ? C.amber : C.green }}>
                      {up ? '↑' : '↓'}{Math.abs(diff).toFixed(1)}h
                    </span>
                  );
                }

                return (
                  <tr
                    key={member.id}
                    onClick={() => isFT && setSel(member)}
                    style={{ borderBottom: `1px solid ${C.border}`, cursor: isFT ? 'pointer' : 'default', background: i % 2 === 0 ? C.white : C.cream }}
                    onMouseEnter={ev => { if (isFT) ev.currentTarget.style.background = C.skyBlueLight; }}
                    onMouseLeave={ev => ev.currentTarget.style.background = i % 2 === 0 ? C.white : C.cream}
                  >
                    <td style={{ padding: '10px 14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <RoleIcon dept={member.dept} size={28} />
                        <span style={{ fontWeight: 600, fontSize: '13px', color: C.charcoal }}>{member.name}</span>
                      </div>
                    </td>
                    <td style={{ padding: '10px 14px', fontSize: '13px', color: C.mid }}>{member.venue}</td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{ background: C.cream, color: C.mid, padding: '2px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: 600 }}>
                        {member.dept}
                      </span>
                    </td>
                    <td style={{ padding: '10px 14px', fontSize: '11px' }}>
                      <span style={{ color: isFT ? C.skyText : C.mid, fontWeight: 600 }}>
                        {isFT ? 'FT' : 'PT'}
                      </span>
                    </td>
                    <td style={{ padding: '10px 14px', fontSize: '13px', color: C.mid }}>
                      {isFT && member.contract_hours ? `${member.contract_hours}hr` : '—'}
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        <span style={{ fontSize: '13px', fontWeight: 600, color: C.charcoal }}>
                          {ot !== null ? `${ot}h` : '—'}
                        </span>
                        <Movement cur={ot} old={prev?.ot_balance} />
                      </div>
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        <span style={{ fontSize: '13px', fontWeight: 600, color: C.charcoal }}>
                          {lv !== null ? `${lv}h` : '—'}
                        </span>
                        <Movement cur={lv} old={prev?.leave_balance} />
                      </div>
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                      <span style={{ fontWeight: 700, fontSize: '13px', color: C.charcoal }}>
                        {tot !== null ? `${tot}h` : '—'}
                      </span>
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                      {isFT && <Mail size={14} color={member.email ? C.skyText : C.border} title={member.email ? 'Send balance link' : 'No email on file'} />}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {sel && (
        <LeaveOTMemberModal
          member={sel}
          currentUser={currentUser}
          allEntries={entries}
          snapshots={snaps}
          onClose={() => setSel(null)}
          onMemberUpdated={updated => setSel(updated)}
          onReloadMembers={onReloadMembers}
        />
      )}

      {ptSel && (
        <PTMemberModal
          member={ptSel}
          currentUser={currentUser}
          onClose={() => setPtSel(null)}
        />
      )}
    </div>
  );
}